import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, X, Plus, Trash2, Copy, Star, Pencil, ShieldCheck, KeyRound, Briefcase, Flag, Ban, Percent, Users, Megaphone, Gavel, RefreshCcw, AlertTriangle, Mail, Eye, ShieldOff, Crown, MessageSquare, AlertCircle, Building2, Sliders, Sparkles, Clock, FileText, Download } from "lucide-react";

export default function AdminDashboard() {
  const { user: me } = useAuth();
  const isOwner = me?.role === "owner";
  const [tab, setTab] = useState("recruiters");
  const [recruiters, setRecruiters] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [userRole, setUserRole] = useState("");
  const [invites, setInvites] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reports, setReports] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [ownerPlan, setOwnerPlan] = useState({ label: "Owner Unlimited", note: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newInvite, setNewInvite] = useState({ note: "", max_uses: 1, role_on_use: "candidate", company_id: "", grant_tier: "" });
  const [sendingInvite, setSendingInvite] = useState(null); // { code, to_email, to_name }
  const [sendInviteError, setSendInviteError] = useState("");
  const [sendInviteSubmitting, setSendInviteSubmitting] = useState(false);
  const [newDiscount, setNewDiscount] = useState({ code: "", percent_off: 20, max_uses: 10, applies_to: "all", note: "" });
  const [newBlacklist, setNewBlacklist] = useState({ pattern: "", reason: "" });
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", body: "", kind: "info", is_active: true });
  const [detailUser, setDetailUser] = useState(null);
  const [actionTarget, setActionTarget] = useState(null); // { user, kind: "warn"|"ban"|"email"|"limit"|"deny" }
  const [actionForm, setActionForm] = useState({ reason: "", note: "", subject: "", body: "" });
  const [editingReport, setEditingReport] = useState(null);
  const [reportNote, setReportNote] = useState("");
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingUser, setEditingUser] = useState(null);  // { user_id, name, email, role, ... }
  const [editingUserSaving, setEditingUserSaving] = useState(false);
  const [editingUserError, setEditingUserError] = useState("");
  const [overrideTarget, setOverrideTarget] = useState(null); // { user_id, email, name, account_overrides, subscription_tier }
  const [overrideForm, setOverrideForm] = useState({ max_jobs: "", max_team_seats: "", max_applications_per_month: "", max_featured_boosts_per_month: "", max_spotlight_boosts_per_month: "", ff_custom_email_templates: "inherit", ff_custom_careers_branding: "inherit", ff_bulk_actions: "inherit", ff_whitelabel: "inherit", ff_custom_pipeline: "inherit", ff_company_tracking: "inherit", quotas_expires_at: "", features_expires_at: "", note: "", custom_tags: "" });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideError, setOverrideError] = useState("");
  // Owner-only: create a standalone company (no recruiter attached)
  const [newCompany, setNewCompany] = useState({ name: "", website: "", location: "", description: "", org_type: "business" });
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newCompanySaving, setNewCompanySaving] = useState(false);
  const [newCompanyError, setNewCompanyError] = useState("");

  // Contract state
  const [adminContracts, setAdminContracts] = useState([]);
  const [adminSelectedContract, setAdminSelectedContract] = useState(null);
  const [contractTemplateText, setContractTemplateText] = useState("");
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingContractTerm, setEditingContractTerm] = useState(null);
  const [editContractStartDate, setEditContractStartDate] = useState("");
  const [editContractEndDate, setEditContractEndDate] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [updatingTerm, setUpdatingTerm] = useState(false);


  // Custom Contract Issuance State (Owner-only)
  const [showIssueCustomContract, setShowIssueCustomContract] = useState(false);
  const [customContractForm, setCustomContractForm] = useState({ user_id: "", tier: "growth", monthly_price_pence: 2499, contract_text: "", start_date: new Date().toISOString().split("T")[0], length_months: 12, discount_percent: 0 });
  const [issuingCustomContract, setIssuingCustomContract] = useState(false);


  // Custom Tracking State
  const [trackers, setTrackers] = useState([]);
  const [newTracker, setNewTracker] = useState({ target_id: "", target_type: "recruiter", title: "Recruiter Account Review", status: "Pending", tracking_code: "", status_color: "#5C5C5C" });
  const [trackerModal, setTrackerModal] = useState(null); // tracker object to edit updates
  const [newUpdate, setNewUpdate] = useState({ status: "", message: "", status_color: "#3B82F6" });
  const [creatingTracker, setCreatingTracker] = useState(false);
  const [addingUpdate, setAddingUpdate] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [r, i, j, rep, bl, dc, au, an, ap, op, co, rv, ct, tpl, trk] = await Promise.all([
        api.get("/admin/recruiters").catch(() => ({ data: [] })),
        api.get("/admin/invites").catch(() => ({ data: [] })),
        api.get("/jobs?mine=true").catch(() => ({ data: [] })),
        api.get("/admin/reports").catch(() => ({ data: [] })),
        api.get("/admin/blacklist").catch(() => ({ data: [] })),
        api.get("/admin/discounts").catch(() => ({ data: [] })),
        api.get("/admin/users").catch(() => ({ data: [] })),
        api.get("/admin/announcements").catch(() => ({ data: [] })),
        api.get("/admin/appeals").catch(() => ({ data: [] })),
        isOwner ? api.get("/admin/me/plan").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        api.get("/companies").catch(() => ({ data: [] })),
        api.get("/admin/recruiter-reviews").catch(() => ({ data: [] })),
        api.get("/admin/contracts").catch(() => ({ data: [] })),
        api.get("/admin/contract-template").catch(() => ({ data: { template_text: "" } })),
        api.get("/admin/trackers").catch(() => ({ data: [] })),
      ]);
      setRecruiters(r.data); setInvites(i.data); setJobs(j.data);
      setReports(rep.data); setBlacklist(bl.data); setDiscounts(dc.data);
      setAllUsers(au.data); setAnnouncements(an.data); setAppeals(ap.data);
      setCompanies(co.data); setReviews(rv.data);
      setAdminContracts(ct.data.contracts || []);
      setContractTemplateText(tpl.data.template_text || "");
      setTrackers(trk.data || []);
      if (op.data) setOwnerPlan(op.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [isOwner]);

  const reinstateDiscount = async (code) => { try { await api.put(`/admin/discounts/${code}/reinstate`); load(); } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); } };
  const reinstateInvite = async (code) => { try { await api.put(`/admin/invites/${code}/reinstate`); load(); } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); } };

  const handleIssueCustomContract = async (e) => {
    e?.preventDefault?.();
    if (!customContractForm.user_id.trim() || !customContractForm.contract_text.trim() || !customContractForm.start_date || !customContractForm.length_months) {
      alert("Please fill in all required fields (User ID, Contract text, Start Date, and Length in Months).");
      return;
    }
    setIssuingCustomContract(true);
    try {
      await api.post("/admin/contracts/create-custom", {
        ...customContractForm,
        monthly_price_pence: parseInt(customContractForm.monthly_price_pence, 10) || 0,
        length_months: parseInt(customContractForm.length_months, 10) || 12,
        discount_percent: parseInt(customContractForm.discount_percent, 10) || 0
      });
      alert("Ad-hoc Custom Contract successfully created and registered!");
      setCustomContractForm({ user_id: "", tier: "growth", monthly_price_pence: 2499, contract_text: "", start_date: new Date().toISOString().split("T")[0], length_months: 12, discount_percent: 0 });
      setShowIssueCustomContract(false);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setIssuingCustomContract(false);
    }
  };


  const handleSaveContractTemplate = async () => {
    setSavingTemplate(true);
    setError("");
    try {
      await api.put("/admin/contract-template", { template_text: contractTemplateText });
      alert("Contract template successfully updated!");
      setShowTemplateEditor(false);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleCreateTracker = async (e) => {
    e?.preventDefault?.();
    if (!newTracker.target_id.trim() || !newTracker.title.trim() || !newTracker.status.trim()) {
      alert("Target ID, Title, and Status are required.");
      return;
    }
    setCreatingTracker(true);
    try {
      await api.post("/admin/trackers", newTracker);
      setNewTracker({ target_id: "", target_type: "recruiter", title: "Recruiter Account Review", status: "Pending", tracking_code: "", status_color: "#5C5C5C" });
      alert("Tracker successfully created!");
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCreatingTracker(false);
    }
  };

  const handleAddTrackerUpdate = async (e) => {
    e?.preventDefault?.();
    if (!newUpdate.status.trim() || !newUpdate.message.trim()) {
      alert("Status and Message are required.");
      return;
    }
    setAddingUpdate(true);
    try {
      await api.post(`/admin/trackers/${trackerModal.tracker_id}/updates`, newUpdate);
      setNewUpdate({ status: "", message: "", status_color: "#3B82F6" });
      alert("Tracker update added!");
      setTrackerModal(null);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAddingUpdate(false);
    }
  };

  const handleDeleteTracker = async (trackerId) => {
    if (!window.confirm("Are you sure you want to delete this tracker?")) return;
    try {
      await api.delete(`/admin/trackers/${trackerId}`);
      alert("Tracker deleted!");
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };


  const handleVoidContract = async (contractId) => {
    if (!window.confirm("Are you sure you want to void this contract? The recruiter's subscription will be immediately reverted to Free and any unpaid associated invoices will be voided.")) return;
    setError("");
    try {
      await api.post(`/admin/contracts/${contractId}/void`);
      alert("Contract successfully voided!");
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleReinstateContract = async (contractId) => {
    if (!window.confirm("Reinstate this voided contract? The recruiter's subscription will be immediately re-activated to the contract tier.")) return;
    setError("");
    try {
      await api.post(`/admin/contracts/${contractId}/reinstate`);
      alert("Contract successfully reinstated!");
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleOpenEditTerm = (ct) => {
    setEditingContractTerm(ct);
    setEditContractStartDate(ct.start_date ? ct.start_date.slice(0, 10) : "");
    setEditContractEndDate(ct.end_date ? ct.end_date.slice(0, 10) : "");
  };

  const handleSaveContractTerm = async () => {
    if (!editContractStartDate || !editContractEndDate) {
      alert("Please enter both start and end dates.");
      return;
    }
    setUpdatingTerm(true);
    setError("");
    try {
      await api.put(`/admin/contracts/${editingContractTerm.contract_id}/term`, {
        start_date: new Date(editContractStartDate).toISOString(),
        end_date: new Date(editContractEndDate).toISOString(),
      });
      alert("Contract term dates successfully updated!");
      setEditingContractTerm(null);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUpdatingTerm(false);
    }
  };
  const changeRole = async (uid, newRole) => {
    if (!window.confirm(`Set this user's role to ${newRole}?`)) return;
    try { await api.put(`/admin/users/${uid}/role`, { role: newRole }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const banUser = async () => {
    try { await api.post(`/admin/users/${actionTarget.user.user_id}/ban`, { reason: actionForm.reason, note: actionForm.note }); setActionTarget(null); setActionForm({ reason: "", note: "", subject: "", body: "" }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const warnUser = async () => {
    try { await api.post(`/admin/users/${actionTarget.user.user_id}/warn`, { reason: actionForm.reason, note: actionForm.note }); setActionTarget(null); setActionForm({ reason: "", note: "", subject: "", body: "" }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const limitUser = async () => {
    try { await api.post(`/admin/users/${actionTarget.user.user_id}/limit`, { reason: actionForm.reason, note: actionForm.note }); setActionTarget(null); setActionForm({ reason: "", note: "", subject: "", body: "" }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const unlimitUser = async (uid) => {
    try { await api.post(`/admin/users/${uid}/unlimit`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const denyRecruiter = async () => {
    try { await api.put(`/admin/recruiters/${actionTarget.user.user_id}/deny`, { reason: actionForm.reason, note: actionForm.note }); setActionTarget(null); setActionForm({ reason: "", note: "", subject: "", body: "" }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const resolveReview = async (id, action) => {
    try { await api.put(`/admin/recruiter-reviews/${id}/resolve`, { action }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const reopenReport = async (rid) => {
    try { await api.put(`/admin/reports/${rid}/reopen`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const addReportNote = async () => {
    if (!reportNote.trim()) return;
    try { await api.post(`/admin/reports/${editingReport.report_id}/note`, { note: reportNote }); setReportNote(""); const r = await api.get("/admin/reports"); setReports(r.data); setEditingReport(r.data.find((x) => x.report_id === editingReport.report_id)); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const editReport = async (patch) => {
    try { const { data } = await api.put(`/admin/reports/${editingReport.report_id}`, patch); setEditingReport(data); const r = await api.get("/admin/reports"); setReports(r.data); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveCompany = async () => {
    try {
      await api.put(`/admin/companies/${editingCompany.company_id}`, {
        name: editingCompany.name,
        website: editingCompany.website || "",
        description: editingCompany.description || "",
        location: editingCompany.location || "",
        owner_user_id: editingCompany.owner_user_id || "",
        members: editingCompany.members || []
      });
      setEditingCompany(null);
      load();
    }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const openEditUser = (u) => {
    setEditingUserError("");
    setEditingUser({
      user_id: u.user_id,
      name: u.name || "",
      email: u.email || "",
      role: u.role || "candidate",
      company_name: u.company_name || u.business_name || "",
      job_title: u.job_title || "",
      phone: u.phone || "",
      linkedin_url: u.linkedin_url || "",
      company_website: u.company_website || "",
      subscription_tier: u.subscription_tier || "free",
      subscription_ends_at: (u.subscription_ends_at || "").slice(0, 10),
      org_type: u.org_type || "business",
      is_approved: !!u.is_approved,
      custom_tags: (u.custom_tags || []).join(", "),
      _current_company_id: u.company_id || "",
      _attach_company_id: u.company_id || "",
    });
  };
  const saveUserEdit = async () => {
    if (!editingUser) return;
    setEditingUserSaving(true); setEditingUserError("");
    try {
      const { user_id, _attach_company_id, _current_company_id, ...patch } = editingUser;
      const custom_tags = editingUser.custom_tags ? editingUser.custom_tags.split(",").map(s => s.trim()).filter(Boolean) : [];
      await api.put(`/admin/users/${user_id}`, { ...patch, custom_tags });
      // If owner picked a different company, attach to it (handles plan inheritance + emails)
      if (_attach_company_id && _attach_company_id !== _current_company_id) {
        await api.post(`/admin/companies/${_attach_company_id}/attach`, {
          user_id, role: patch.role || "recruiter",
        });
      }
      setEditingUser(null);
      load();
    } catch (e) {
      setEditingUserError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setEditingUserSaving(false); }
  };
  const deleteCompany = async (cid) => {
    if (!window.confirm("Delete this company? Members keep their accounts.")) return;
    try { await api.delete(`/admin/companies/${cid}`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const createCompany = async () => {
    if (!newCompany.name.trim()) { setNewCompanyError("Company name is required"); return; }
    setNewCompanySaving(true); setNewCompanyError("");
    try {
      await api.post("/admin/companies", {
        name: newCompany.name.trim(),
        website: newCompany.website.trim(),
        location: newCompany.location.trim(),
        description: newCompany.description.trim(),
        org_type: newCompany.org_type || "business",
      });
      setNewCompany({ name: "", website: "", location: "", description: "", org_type: "business" });
      setNewCompanyOpen(false);
      load();
    } catch (e) {
      setNewCompanyError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setNewCompanySaving(false); }
  };
  const emailUser = async () => {
    try {
      const splitEmails = (s) => (s || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
      const cc = splitEmails(actionForm.cc);
      const bcc = splitEmails(actionForm.bcc);
      const { data } = await api.post(`/admin/users/${actionTarget.user.user_id}/email`, {
        subject: actionForm.subject, body: actionForm.body,
        cc, bcc,
      });
      setActionTarget(null);
      setActionForm({ reason: "", note: "", subject: "", body: "", cc: "", bcc: "" });
      const ccCount = (data.cc || []).length;
      const bccCount = (data.bcc || []).length;
      const summary = (ccCount || bccCount)
        ? ` · ${ccCount} CC, ${bccCount} BCC`
        : "";
      alert(data.sent
        ? `Message delivered.${summary}`
        : `Message logged${summary} — but email service was unavailable (Resend not configured).`);
    }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const unbanUser = async (uid) => {
    if (!window.confirm("Reinstate this user?")) return;
    try { await api.post(`/admin/users/${uid}/unban`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const adminResetPassword = async (uid, label) => {
    if (!window.confirm(`Reset password for ${label}? A new random password will be generated.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${uid}/reset-password`, {});
      alert(`New password for ${label}:\n\n${data.new_password}\n\nCopy this now — it won't be shown again.`);
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const viewDetail = async (uid) => {
    try { const { data } = await api.get(`/admin/users/${uid}/detail`); setDetailUser(data); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const createAnnouncement = async () => {
    if (!newAnnouncement.title.trim()) return;
    try { await api.post("/admin/announcements", newAnnouncement); setNewAnnouncement({ title: "", body: "", kind: "info", is_active: true }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const toggleAnnouncement = async (a) => {
    try { await api.put(`/admin/announcements/${a.announcement_id}`, { title: a.title, body: a.body, kind: a.kind, is_active: !a.is_active }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/admin/announcements/${id}`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const resolveAppeal = async (id, action, note = "") => {
    try { await api.put(`/admin/appeals/${id}/resolve`, { action, note }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveOwnerPlan = async () => {
    try { await api.post("/admin/me/plan", ownerPlan); alert("Saved."); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openOverride = async (u) => {
    setOverrideError("");
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/overrides`);
      const ov = data.account_overrides || {};
      const ff = ov.feature_flags || {};
      setOverrideTarget({
        user_id: u.user_id, email: u.email, name: u.name,
        subscription_tier: u.subscription_tier,
        tier_feature_defaults: data.tier_feature_defaults || {},
        effective_features: data.effective_features || {},
      });
      const stringify = (v) => v === true ? "on" : v === false ? "off" : "inherit";
      setOverrideForm({
        max_jobs: ov.max_jobs == null ? "" : String(ov.max_jobs),
        max_team_seats: ov.max_team_seats == null ? "" : String(ov.max_team_seats),
        max_applications_per_month: ov.max_applications_per_month == null ? "" : String(ov.max_applications_per_month),
        max_featured_boosts_per_month: ov.max_featured_boosts_per_month == null ? "" : String(ov.max_featured_boosts_per_month),
        max_spotlight_boosts_per_month: ov.max_spotlight_boosts_per_month == null ? "" : String(ov.max_spotlight_boosts_per_month),
        // 3-state feature toggles: inherit (default), on, off
        ff_custom_email_templates: stringify(ff.custom_email_templates),
        ff_custom_careers_branding: stringify(ff.custom_careers_branding),
        ff_bulk_actions: stringify(ff.bulk_actions),
        ff_whitelabel: stringify(ff.whitelabel),
        ff_custom_pipeline: stringify(ff.custom_pipeline),
        ff_company_tracking: stringify(ff.company_tracking),
        // Convert ISO datetime → yyyy-mm-dd for <input type="date">
        quotas_expires_at: (data.account_overrides_quotas_expires_at || "").slice(0, 10),
        features_expires_at: (data.account_overrides_features_expires_at || "").slice(0, 10),
        note: data.account_overrides_note || "",
        custom_tags: (data.custom_tags || []).join(", "),
      });
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveOverride = async () => {
    if (!overrideTarget) return;
    setOverrideSaving(true); setOverrideError("");
    const parse = (v) => {
      const t = (v || "").toString().trim();
      if (t === "") return null;
      const n = parseInt(t, 10);
      return Number.isNaN(n) ? null : n;
    };
    const ffParse = (s) => s === "on" ? true : s === "off" ? false : null;
    const feature_flags = {
      custom_email_templates: ffParse(overrideForm.ff_custom_email_templates),
      custom_careers_branding: ffParse(overrideForm.ff_custom_careers_branding),
      bulk_actions: ffParse(overrideForm.ff_bulk_actions),
      whitelabel: ffParse(overrideForm.ff_whitelabel),
      custom_pipeline: ffParse(overrideForm.ff_custom_pipeline),
      company_tracking: ffParse(overrideForm.ff_company_tracking),
    };
    const anyFlag = Object.values(feature_flags).some((v) => v !== null);
    const payload = {
      max_jobs: parse(overrideForm.max_jobs),
      max_team_seats: parse(overrideForm.max_team_seats),
      max_applications_per_month: parse(overrideForm.max_applications_per_month),
      max_featured_boosts_per_month: parse(overrideForm.max_featured_boosts_per_month),
      max_spotlight_boosts_per_month: parse(overrideForm.max_spotlight_boosts_per_month),
      // Always include feature_flags (even when all-null) so saving "all Inherit"
      // explicitly clears any previously-persisted forces.
      feature_flags: anyFlag ? feature_flags : {},
      // Convert yyyy-mm-dd → end-of-day UTC ISO so the override stays active
      // through the *whole* of the chosen day on the user's timezone.
      quotas_expires_at: overrideForm.quotas_expires_at ? `${overrideForm.quotas_expires_at}T23:59:59+00:00` : "",
      features_expires_at: overrideForm.features_expires_at ? `${overrideForm.features_expires_at}T23:59:59+00:00` : "",
      note: overrideForm.note || "",
    };
    try {
      await api.put(`/admin/users/${overrideTarget.user_id}/overrides`, payload);
      setOverrideTarget(null); load();
    } catch (e) {
      setOverrideError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setOverrideSaving(false); }
  };
  const clearOverrides = async () => {
    if (!overrideTarget) return;
    if (!window.confirm("Remove all custom quotas? The user will fall back to their plan's defaults.")) return;
    setOverrideSaving(true); setOverrideError("");
    try {
      await api.put(`/admin/users/${overrideTarget.user_id}/overrides`, { note: "" });
      setOverrideTarget(null); load();
    } catch (e) {
      setOverrideError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setOverrideSaving(false); }
  };

  // Build a friendly "you've been awarded a free plan" email body from the
  // current override form state — Owner can still edit before sending.
  const buildAwardEmail = () => {
    const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "";
    const quotaLabels = {
      max_jobs: "Active jobs",
      max_team_seats: "Team seats",
      max_applications_per_month: "Applications per month",
      max_featured_boosts_per_month: "Featured boosts per month",
      max_spotlight_boosts_per_month: "Spotlight boosts per month",
    };
    const featLabels = {
      ff_custom_email_templates: "Custom email templates",
      ff_custom_careers_branding: "Custom careers page branding",
      ff_bulk_actions: "Bulk applicant actions",
      ff_whitelabel: "White-label mode (hide platform branding)",
      ff_custom_pipeline: "Custom pipeline stages (Growth+)",
      ff_company_tracking: "Company progress tracking (Enterprise+)",
    };
    const quotaLines = [];
    Object.keys(quotaLabels).forEach((k) => {
      const v = overrideForm[k];
      if (v !== "" && v != null) {
        quotaLines.push(`• ${quotaLabels[k]}: ${v === "-1" || v === -1 ? "Unlimited" : v}`);
      }
    });
    const featLines = [];
    Object.keys(featLabels).forEach((k) => {
      if (overrideForm[k] === "on") featLines.push(`• ${featLabels[k]}`);
    });
    const parts = [];
    parts.push(`Hi ${(overrideTarget?.name || "there").split(" ")[0]},`);
    parts.push("");
    parts.push(`Great news — we've upgraded your Ocean Ash account with the following perks at no extra cost:`);
    parts.push("");
    if (quotaLines.length) {
      parts.push("Custom quotas:");
      parts.push(...quotaLines);
      if (overrideForm.quotas_expires_at) parts.push(`(Active until ${fmtDate(overrideForm.quotas_expires_at)})`);
      parts.push("");
    }
    if (featLines.length) {
      parts.push("Bonus features:");
      parts.push(...featLines);
      if (overrideForm.features_expires_at) parts.push(`(Active until ${fmtDate(overrideForm.features_expires_at)})`);
      parts.push("");
    }
    if (!quotaLines.length && !featLines.length) {
      parts.push("(No active overrides — set some above before sending.)");
      parts.push("");
    }
    parts.push("Head over to your Billing page to see your active limits and bonus features in action.");
    parts.push("");
    parts.push("Welcome to the upgrade!");
    return {
      subject: "You've been awarded a free upgrade",
      body: parts.join("\n"),
    };
  };
  const notifyAward = () => {
    if (!overrideTarget) return;
    const { subject, body } = buildAwardEmail();
    setActionTarget({ user: { user_id: overrideTarget.user_id, name: overrideTarget.name, email: overrideTarget.email }, kind: "email" });
    setActionForm({ reason: "", note: "", subject, body, cc: "", bcc: "" });
    setOverrideTarget(null);
  };

  const approve = async (uid, approveBool) => {
    try {
      await api.put(`/admin/recruiters/${uid}/approve`, { approve: approveBool });
      load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const removeRec = async (uid) => {
    if (!window.confirm("Reject and remove this recruiter account?")) return;
    try {
      await api.delete(`/admin/recruiters/${uid}`);
      load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const deleteUser = async (uid, label) => {
    if (!window.confirm(`Delete user ${label}? This removes their account and applications.`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const createInvite = async () => {
    try { await api.post("/admin/invites", newInvite); setNewInvite({ note: "", max_uses: 1, role_on_use: "candidate", company_id: "", grant_tier: "" }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const doSendInvite = async (e) => {
    e?.preventDefault?.();
    if (!sendingInvite) return;
    setSendInviteError(""); setSendInviteSubmitting(true);
    try {
      await api.post(`/admin/invites/${sendingInvite.code}/send`, {
        to_email: sendingInvite.to_email.toLowerCase().trim(),
        to_name: sendingInvite.to_name || "",
      });
      setSendingInvite(null);
      load();
      alert(`Invite emailed to ${sendingInvite.to_email}.`);
    } catch (e) {
      setSendInviteError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSendInviteSubmitting(false); }
  };
  const revokeInvite = async (code) => { await api.delete(`/admin/invites/${code}`); load(); };
  const prioritise = async (jobId, patch) => {
    try { await api.put(`/admin/jobs/${jobId}/prioritise`, patch); load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const resolveReport = async (rid, action, note = "") => {
    await api.put(`/admin/reports/${rid}/resolve`, { action, note });
    load();
  };
  const addBlacklist = async () => {
    if (!newBlacklist.pattern.trim()) return;
    try { await api.post("/admin/blacklist", newBlacklist); setNewBlacklist({ pattern: "", reason: "" }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const removeBlacklist = async (p) => { await api.delete(`/admin/blacklist/${encodeURIComponent(p)}`); load(); };
  const createDiscount = async () => {
    try { await api.post("/admin/discounts", newDiscount); setNewDiscount({ code: "", percent_off: 20, max_uses: 10, applies_to: "all", note: "" }); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const revokeDiscount = async (code) => { await api.delete(`/admin/discounts/${code}`); load(); };

  const pending = useMemo(
    () => recruiters.filter((r) => !r.is_approved && !r.is_denied),
    [recruiters]
  );
  const approved = useMemo(
    () => recruiters.filter((r) => r.is_approved),
    [recruiters]
  );
  const filteredUsers = useMemo(() => {
    return allUsers.filter((u) => {
      if (userRole && u.role !== userRole) return false;
      if (userQuery) {
        const q = userQuery.toLowerCase();
        return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q) || (u.company_name || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [allUsers, userRole, userQuery]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            {isOwner ? <Crown className="w-3 h-3 text-[var(--brand-primary)]" /> : <ShieldCheck className="w-3 h-3" />}
            {isOwner ? "Owner console" : "Moderator console"}
          </div>
          <h1 className="font-display font-black text-5xl mt-3 tracking-tight">
            {isOwner ? "Run the company." : "Keep things tidy."}
          </h1>
          {isOwner && (
            <div className="text-xs text-[#5C5C5C] mt-2 inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--brand-primary)] bg-[var(--brand-primary)]/5">
              <Crown className="w-3 h-3 text-[var(--brand-primary)]" /> {ownerPlan.label}
            </div>
          )}
          {!isOwner && (
            <div className="text-xs text-[#5C5C5C] mt-2 inline-flex items-center gap-1.5 px-2 py-1 border border-[#0F0F0F] bg-[#0F0F0F]/5">
              <ShieldCheck className="w-3 h-3" /> Moderator — limited admin scope
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/settings" className="btn-ghost text-sm">Settings</Link>
          {isOwner && (
            <Link to="/admin/plans" data-testid="admin-plans-link" className="btn-ghost text-sm">Plans &amp; invoices</Link>
          )}
          {isOwner && (
            <Link to="/recruiter/jobs/new" data-testid="admin-new-job" className="btn-terra">
              <Plus className="w-4 h-4" /> Post role
            </Link>
          )}
        </div>
      </div>

      <div className="mt-8 border-b border-[#E2E0D8] overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {[
            { v: "recruiters", t: "Recruiters", icon: ShieldCheck, badge: pending.length, ownerOnly: false },
            { v: "users", t: "All users", icon: Users, badge: allUsers.length, ownerOnly: false },
            { v: "companies", t: "Companies", icon: Briefcase, badge: companies.length, ownerOnly: false },
            { v: "reports", t: "Reports", icon: Flag, badge: reports.filter((r) => r.status === "open" || r.status === "reopened").length, ownerOnly: false },
            { v: "appeals", t: "Appeals", icon: Gavel, badge: appeals.filter((a) => a.status === "open").length, ownerOnly: false },
            { v: "reviews", t: "Review requests", icon: MessageSquare, badge: reviews.filter((r) => r.status === "open").length, ownerOnly: false },
            { v: "blacklist", t: "Blacklist", icon: Ban, badge: blacklist.length, ownerOnly: false },
            { v: "moderators", t: "Moderators", icon: ShieldCheck, badge: allUsers.filter((u) => u.role === "moderator").length, ownerOnly: true },
            { v: "announcements", t: "Announcements", icon: Megaphone, badge: announcements.filter((a) => a.is_active).length, ownerOnly: false },
            { v: "invites", t: "Invite codes", icon: KeyRound, badge: invites.filter((i) => !i.is_revoked).length, ownerOnly: true },
            { v: "discounts", t: "Discounts", icon: Percent, badge: discounts.filter((d) => !d.is_revoked).length, ownerOnly: true },
            { v: "jobs", t: "Listings", icon: Briefcase, badge: jobs.length, ownerOnly: true },
            { v: "myplan", t: "My plan", icon: Crown, badge: 0, ownerOnly: true },
            { v: "contracts", t: "Contracts", icon: FileText, badge: adminContracts.length, ownerOnly: true },
            { v: "trackers", t: "Custom Tracking", icon: Clock, badge: trackers.length, ownerOnly: false },
          ].filter((t) => isOwner || !t.ownerOnly).map((t) => {
            const Icon = t.icon;
            const active = tab === t.v;
            return (
              <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}
                className={`px-5 py-3 font-display font-bold text-sm border-b-2 -mb-px flex items-center gap-2 ${active ? "border-[var(--brand-primary)] text-[#0F0F0F]" : "border-transparent text-[#5C5C5C] hover:text-[#0F0F0F]"}`}>
                <Icon className="w-4 h-4" /> {t.t}
                {t.badge > 0 && <span className={`text-[10px] px-1.5 py-0.5 ${active ? "bg-[var(--brand-primary)] text-white" : "bg-[#E2E0D8]"}`}>{t.badge}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="mt-4 text-sm text-[var(--brand-primary)]">{error}</div>}
      {loading && <div className="mt-10 text-[#5C5C5C]">Loading…</div>}

      {!loading && tab === "recruiters" && (
        <div className="mt-8 space-y-10">
          <section>
            <div className="label-eyebrow mb-3">Pending approval — {pending.length}</div>
            <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
              {pending.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No recruiters waiting.</div>}
              {pending.map((r) => (
                <div key={r.user_id} data-testid={`pending-${r.user_id}`} className="bg-[#FDFCF8] p-5 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold">{r.name}</div>
                    <div className="text-sm text-[#5C5C5C]">{r.email}</div>
                    {(r.company_name || r.job_title) && (
                      <div className="mt-2 text-xs space-y-0.5">
                        {r.job_title && <div><span className="label-eyebrow mr-1">Title</span>{r.job_title}</div>}
                        {r.company_name && <div><span className="label-eyebrow mr-1">Company</span>{r.company_name}</div>}
                        {r.phone && <div><span className="label-eyebrow mr-1">Phone</span>{r.phone}</div>}
                        {r.linkedin_url && <div><span className="label-eyebrow mr-1">LinkedIn</span><a href={r.linkedin_url} target="_blank" rel="noreferrer" className="underline hover:text-[var(--brand-primary)]">{r.linkedin_url}</a></div>}
                      </div>
                    )}
                    <div className="text-xs text-[#5C5C5C] mt-2">Joined {new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => approve(r.user_id, true)} data-testid={`approve-${r.user_id}`} className="btn-terra text-sm py-2 px-3"><Check className="w-4 h-4" /> Approve</button>
                    <button onClick={() => { setActionTarget({ user: r, kind: "deny" }); setActionForm({ reason: "", note: "", subject: "", body: "" }); }} data-testid={`deny-${r.user_id}`} className="text-sm py-2 px-3 border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white"><ShieldOff className="w-4 h-4" /> Deny</button>
                    <button onClick={() => removeRec(r.user_id)} data-testid={`reject-${r.user_id}`} className="btn-ghost text-sm py-2 px-3"><X className="w-4 h-4" /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section data-testid="denied-section">
            <div className="label-eyebrow mb-3">Denied recruiters — {recruiters.filter((r) => r.is_denied).length}</div>
            <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
              {recruiters.filter((r) => r.is_denied).length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">None.</div>}
              {recruiters.filter((r) => r.is_denied).map((r) => (
                <div key={r.user_id} data-testid={`denied-${r.user_id}`} className="bg-[#FDFCF8] p-5 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold">{r.name} <span className="text-xs text-[var(--brand-primary)] font-normal ml-2">DENIED</span></div>
                    <div className="text-sm text-[#5C5C5C]">{r.email} · {r.company_name}</div>
                    <div className="text-xs text-[#5C5C5C] mt-1">Reason: {r.ban_reason /* served via separate field */}</div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => approve(r.user_id, true)} data-testid={`reinstate-recruiter-${r.user_id}`} className="btn-terra text-sm py-2 px-3"><RefreshCcw className="w-4 h-4" /> Reinstate</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section>
            <div className="label-eyebrow mb-3">Approved recruiters — {approved.length}</div>
            <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
              {approved.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No approved recruiters yet.</div>}
              {approved.map((r) => (
                <div key={r.user_id} className="bg-[#FDFCF8] p-5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold">{r.name} <span className="font-normal text-xs text-[#5C5C5C] ml-2">{r.company_name || ""}</span></div>
                    <div className="text-sm text-[#5C5C5C]">{r.email} · {r.subscription_tier || "free"}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => approve(r.user_id, false)} data-testid={`revoke-${r.user_id}`} className="text-sm font-semibold hover:text-[var(--brand-primary)] px-3 py-2">Revoke</button>
                    <button onClick={() => removeRec(r.user_id)} className="text-sm font-semibold hover:text-[var(--brand-primary)] px-3 py-2 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {!loading && tab === "users" && (
        <div className="mt-8">
          <div className="flex gap-3 mb-4 flex-wrap items-center">
            <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Search by name, email, company…"
              data-testid="users-search"
              className="border border-[#0F0F0F] px-3 py-2 bg-transparent flex-1 min-w-[200px]" />
            <select value={userRole} onChange={(e) => setUserRole(e.target.value)} data-testid="users-role-filter"
              className="border border-[#0F0F0F] px-3 py-2 bg-transparent">
              <option value="">All roles</option>
              <option value="owner">Owner</option>
              <option value="recruiter">Recruiters</option>
              <option value="candidate">Candidates</option>
            </select>
            <span className="text-sm text-[#5C5C5C]">{filteredUsers.length} of {allUsers.length}</span>
          </div>
          <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {filteredUsers.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No users match.</div>}
            {filteredUsers.map((u) => (
              <div key={u.user_id} data-testid={`user-${u.user_id}`} className={`bg-[#FDFCF8] p-4 grid grid-cols-12 gap-3 items-center ${u.is_banned ? "opacity-60" : ""}`}>
                <div className="col-span-12 md:col-span-4 min-w-0">
                  <div className="font-display font-bold truncate">{u.name || "—"}</div>
                  <div className="text-sm text-[#5C5C5C] truncate">{u.email}</div>
                  {u.account_number && (
                    <div className="text-[10px] font-mono text-[#5C5C5C] bg-[#F3F2EB] px-1.5 py-0.5 border border-[#E2E0D8] inline-block mt-0.5" data-testid={`admin-user-acc-${u.user_id}`}>
                      Acc: <strong>{u.account_number}</strong>
                    </div>
                  )}
                  {u.last_login_at && <div className="text-[10px] text-[#5C5C5C] mt-0.5">Last login {new Date(u.last_login_at).toLocaleDateString()} · {u.last_login_ip || "—"}</div>}
                </div>
                <div className="col-span-6 md:col-span-3 text-xs flex flex-wrap gap-1">
                  <span className={`uppercase tracking-widest px-1.5 py-0.5 ${u.role === "owner" ? "bg-[#0F0F0F] text-white" : u.role === "moderator" ? "bg-[var(--brand-primary)] text-white" : u.role === "recruiter" ? "border border-[var(--brand-primary)] text-[var(--brand-primary)]" : "bg-[#E2E0D8]"}`}>{u.role}</span>
                  {u.role === "recruiter" && (
                    <span className={`uppercase tracking-widest px-1.5 py-0.5 ${u.is_approved ? "bg-green-100 text-green-800" : u.is_denied ? "bg-[var(--brand-primary)] text-white" : "bg-amber-100 text-amber-900"}`}>{u.is_approved ? "approved" : u.is_denied ? "denied" : "pending"}</span>
                  )}
                  {u.is_banned && <span data-testid={`badge-banned-${u.user_id}`} className="uppercase tracking-widest px-1.5 py-0.5 bg-[var(--brand-primary)] text-white">banned</span>}
                  {u.is_limited && <span data-testid={`badge-limited-${u.user_id}`} className="uppercase tracking-widest px-1.5 py-0.5 bg-amber-500 text-white">limited</span>}
                  {(u.warnings_count || 0) > 0 && <span className="uppercase tracking-widest px-1.5 py-0.5 bg-amber-100 text-amber-900">{u.warnings_count}⚠</span>}
                  {u.custom_tags && u.custom_tags.map((tag) => (
                    <span key={tag} className="uppercase tracking-widest px-1.5 py-0.5 bg-blue-100 text-blue-800 font-semibold" data-testid={`admin-user-tag-${tag}-${u.user_id}`}>{tag}</span>
                  ))}
                  {u.role === "recruiter" && u.subscription_tier && u.subscription_tier !== "free" && (
                    <span data-testid={`plan-${u.user_id}`} className="uppercase tracking-widest px-1.5 py-0.5 bg-[#0F0F0F] text-white">plan: {u.subscription_tier}</span>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-[#5C5C5C] truncate">
                  <div className="truncate">{u.company_name || ""}</div>
                  {u.role === "recruiter" && u.subscription_ends_at && (() => {
                    const ends = new Date(u.subscription_ends_at);
                    const isFuture = ends > new Date();
                    return (
                      <div data-testid={`tier-expires-on-${u.user_id}`}
                           className={`text-[10px] mt-0.5 inline-flex items-center gap-1 font-semibold ${isFuture ? "text-amber-700" : "text-[var(--brand-primary)]"}`}
                           title={`Owner-granted ${u.subscription_tier || "free"} plan ends on this date. The user is auto-swept back to ${u.subscription_tier_revert_to || "free"}.`}>
                        <Clock className="w-2.5 h-2.5" /> Expires {ends.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    );
                  })()}
                  {u.role === "recruiter" && !u.subscription_ends_at && (u.subscription_tier || "free") !== "free" && (() => {
                    const cancelDate = u.subscription_cancel_at && new Date(u.subscription_cancel_at) > new Date() ? u.subscription_cancel_at : "";
                    const renewalDate = u.subscription_current_period_end || "";
                    if (cancelDate) {
                      return <div data-testid={`cancel-on-${u.user_id}`} className="text-[10px] text-amber-700 mt-0.5">Cancels {new Date(cancelDate).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>;
                    }
                    if (renewalDate) {
                      return <div data-testid={`renews-on-${u.user_id}`} className="text-[10px] mt-0.5">Renews {new Date(renewalDate).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>;
                    }
                    return null;
                  })()}
                  {u.role === "recruiter" && u.account_overrides && Object.keys(u.account_overrides).length > 0 && (
                    <div data-testid={`overrides-tag-${u.user_id}`} className="text-[10px] mt-0.5 inline-flex items-center gap-1 text-[var(--brand-primary)] font-semibold"
                         title={`Custom quotas active${u.account_overrides_note ? ` — "${u.account_overrides_note}"` : ""}`}>
                      <Sparkles className="w-2.5 h-2.5" /> Custom quotas
                    </div>
                  )}
                </div>
                <div className="col-span-12 md:col-span-3 flex justify-end gap-1 flex-wrap items-center">
                  {isOwner && u.role !== "owner" && u.user_id !== me?.user_id && (
                    <select
                      data-testid={`role-select-${u.user_id}`}
                      value={u.role}
                      onChange={(e) => {
                        const newRole = e.target.value;
                        if (newRole === u.role) return;
                        if (!window.confirm(`Set this user's role to ${newRole}? They will be moved to that section immediately.`)) return;
                        changeRole(u.user_id, newRole);
                      }}
                      title="Change role"
                      className="text-xs border border-[#0F0F0F] bg-transparent px-1.5 py-1 mr-1"
                    >
                      <option value="candidate">candidate</option>
                      <option value="recruiter">recruiter</option>
                      <option value="moderator">moderator</option>
                    </select>
                  )}
                  <button onClick={() => viewDetail(u.user_id)} data-testid={`view-${u.user_id}`} title="View details" className="p-1.5 hover:text-[var(--brand-primary)]"><Eye className="w-4 h-4" /></button>
                  {isOwner && u.role !== "owner" && (
                    <button onClick={() => openEditUser(u)} data-testid={`edit-user-${u.user_id}`} title="Edit user info" className="p-1.5 hover:text-[var(--brand-primary)]"><Pencil className="w-4 h-4" /></button>
                  )}
                  {isOwner && u.role === "recruiter" && (
                    <button onClick={() => openOverride(u)} data-testid={`override-${u.user_id}`} title="Custom plan quotas (override max jobs, seats, boosts…)" className="p-1.5 hover:text-[var(--brand-primary)]"><Sliders className="w-4 h-4" /></button>
                  )}
                  {u.role !== "owner" && !u.is_banned && (
                    <>
                      <button onClick={() => { setActionTarget({ user: u, kind: "warn" }); setActionForm({ reason: "", note: "", subject: "", body: "" }); }} data-testid={`warn-${u.user_id}`} title="Warn" className="p-1.5 hover:text-amber-600"><AlertTriangle className="w-4 h-4" /></button>
                      {u.is_limited
                        ? <button onClick={() => unlimitUser(u.user_id)} data-testid={`unlimit-${u.user_id}`} title="Remove limit" className="p-1.5 hover:text-green-700"><RefreshCcw className="w-4 h-4" /></button>
                        : <button onClick={() => { setActionTarget({ user: u, kind: "limit" }); setActionForm({ reason: "", note: "", subject: "", body: "" }); }} data-testid={`limit-${u.user_id}`} title="Limit" className="p-1.5 hover:text-amber-600"><AlertCircle className="w-4 h-4" /></button>
                      }
                      <button onClick={() => { setActionTarget({ user: u, kind: "ban" }); setActionForm({ reason: "", note: "", subject: "", body: "" }); }} data-testid={`ban-${u.user_id}`} title="Ban" className="p-1.5 hover:text-[var(--brand-primary)]"><Ban className="w-4 h-4" /></button>
                    </>
                  )}
                  {u.role !== "owner" && u.is_banned && (
                    <button onClick={() => unbanUser(u.user_id)} data-testid={`unban-${u.user_id}`} title="Reinstate" className="p-1.5 hover:text-green-700"><RefreshCcw className="w-4 h-4" /></button>
                  )}
                  {u.role !== "owner" && (
                    <button onClick={() => { setActionTarget({ user: u, kind: "email" }); setActionForm({ reason: "", note: "", subject: "", body: "", cc: "", bcc: "" }); }} data-testid={`email-${u.user_id}`} title="Email user" className="p-1.5 hover:text-[var(--brand-primary)]"><Mail className="w-4 h-4" /></button>
                  )}
                  {isOwner && u.role !== "owner" && !u.email_verified && (
                    <>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Manually verify email for ${u.email}?`)) return;
                          try {
                            await api.post(`/admin/users/${u.user_id}/verify`);
                            alert("User successfully verified!");
                            load();
                          } catch (e) { alert(e.response?.data?.detail || e.message); }
                        }}
                        data-testid={`admin-manually-verify-${u.user_id}`}
                        title="Manually Verify User Email"
                        className="text-[10px] font-bold uppercase tracking-wider py-1 px-1.5 border border-green-600 text-green-700 hover:bg-green-600 hover:text-white transition"
                      >
                        Verify Email
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Send a verification link email to ${u.email}?`)) return;
                          try {
                            await api.post(`/admin/users/${u.user_id}/send-verification`);
                            alert("Verification email successfully sent!");
                            load();
                          } catch (e) { alert(e.response?.data?.detail || e.message); }
                        }}
                        data-testid={`admin-send-verification-${u.user_id}`}
                        title="Send Verification Email Link"
                        className="text-[10px] font-bold uppercase tracking-wider py-1 px-1.5 border border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white transition"
                      >
                        Send Link
                      </button>
                    </>
                  )}
                  {isOwner && u.role !== "owner" && u.auth_provider === "local" && (
                    <button onClick={() => adminResetPassword(u.user_id, u.email)} data-testid={`pwd-${u.user_id}`} title="Reset password" className="p-1.5 hover:text-[var(--brand-primary)]"><KeyRound className="w-4 h-4" /></button>
                  )}
                  {isOwner && u.role !== "owner" && (
                    <button onClick={() => deleteUser(u.user_id, u.email)} data-testid={`delete-user-${u.user_id}`} title="Remove user" className="p-1.5 hover:text-[var(--brand-primary)]"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "invites" && (
        <div className="mt-8">
          <section className="border border-[#0F0F0F] p-6">
            <div className="label-eyebrow">Generate invite code</div>
            <p className="text-xs text-[#5C5C5C] mt-1">Set <code>max_uses=0</code> for unlimited uses. Recruiter invites can pre-assign a company and grant a paid plan for free.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <input value={newInvite.note} onChange={(e) => setNewInvite({ ...newInvite, note: e.target.value })}
                data-testid="invite-note"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent sm:col-span-2" placeholder="Note (private)" />
              <input type="number" min={0} value={newInvite.max_uses}
                onChange={(e) => setNewInvite({ ...newInvite, max_uses: parseInt(e.target.value || "0") })}
                data-testid="invite-max-uses"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent" placeholder="Max uses (0=∞)" />
              <select value={newInvite.role_on_use} onChange={(e) => setNewInvite({ ...newInvite, role_on_use: e.target.value })}
                data-testid="invite-role-on-use"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent">
                <option value="candidate">Candidate</option>
                <option value="recruiter">Recruiter</option>
                <option value="moderator">Moderator</option>
              </select>
              <select value={newInvite.company_id} onChange={(e) => setNewInvite({ ...newInvite, company_id: e.target.value })}
                data-testid="invite-company"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent">
                <option value="">No company link</option>
                {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
              </select>
              <select value={newInvite.grant_tier} onChange={(e) => setNewInvite({ ...newInvite, grant_tier: e.target.value })}
                data-testid="invite-grant-tier"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent">
                <option value="">No paid grant (free tier)</option>
                <option value="starter">Grant Starter (free)</option>
                <option value="growth">Grant Growth (free)</option>
                <option value="enterprise">Grant Enterprise (free)</option>
              </select>
            </div>
            <button onClick={createInvite} data-testid="create-invite" className="btn-terra mt-4"><Plus className="w-4 h-4" /> Generate</button>
          </section>
          <div className="mt-8 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {invites.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No invite codes yet.</div>}
            {invites.map((inv) => (
              <div key={inv.code} data-testid={`invite-${inv.code}`} className="bg-[#FDFCF8] p-5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono font-bold text-xl tracking-widest">{inv.code}</div>
                  <div className="text-xs text-[#5C5C5C] mt-1 flex flex-wrap gap-2 items-center">
                    <span className={`uppercase tracking-widest font-bold ${inv.role_on_use === "moderator" ? "text-[var(--brand-primary)]" : (inv.role_on_use === "recruiter" ? "text-[#0F0F0F]" : "")}`}>{inv.role_on_use || "candidate"}</span>
                    {inv.company_id && <span className="border border-[#0F0F0F] px-1.5 py-0.5">company-locked</span>}
                    {inv.grant_tier && <span className="bg-[var(--brand-primary)] text-white px-1.5 py-0.5">grants {inv.grant_tier}</span>}
                    <span>· {inv.note || "—"}</span>
                    <span>· {inv.uses}/{inv.max_uses === 0 ? "∞" : inv.max_uses} used{inv.is_revoked ? " · REVOKED" : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => navigator.clipboard?.writeText(inv.code)} title="Copy code" className="p-2 hover:text-[var(--brand-primary)]"><Copy className="w-4 h-4" /></button>
                  <button onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/invite/${inv.code}`)} data-testid={`invite-share-${inv.code}`} title="Copy share link" className="text-xs px-2 py-1 border border-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Share link</button>
                  <button onClick={() => { setSendingInvite({ code: inv.code, to_email: "", to_name: "" }); setSendInviteError(""); }} data-testid={`invite-send-${inv.code}`} title="Send by email" className="text-xs px-2 py-1 border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Send</button>
                  {!inv.is_revoked
                    ? <button onClick={() => revokeInvite(inv.code)} className="text-sm font-semibold hover:text-[var(--brand-primary)]">Revoke</button>
                    : <button onClick={() => reinstateInvite(inv.code)} data-testid={`reinstate-invite-${inv.code}`} className="text-sm font-semibold text-green-700 hover:underline inline-flex items-center gap-1"><RefreshCcw className="w-3 h-3" /> Reinstate</button>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "discounts" && (
        <div className="mt-8">
          <section className="border border-[#0F0F0F] p-6">
            <div className="label-eyebrow">Create discount code</div>
            <p className="text-xs text-[#5C5C5C] mt-1">Discounts are issued at the owner&apos;s discretion — no guarantee. Set <code>max_uses=0</code> for unlimited.</p>
            <div className="grid sm:grid-cols-5 gap-3 mt-3">
              <input value={newDiscount.code} onChange={(e) => setNewDiscount({ ...newDiscount, code: e.target.value })}
                data-testid="discount-code-input" placeholder="CHARITY20 (optional)"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent uppercase" />
              <input type="number" min={1} max={100} value={newDiscount.percent_off}
                onChange={(e) => setNewDiscount({ ...newDiscount, percent_off: parseInt(e.target.value || "20") })}
                data-testid="discount-percent"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent" placeholder="% off" />
              <input type="number" min={0} value={newDiscount.max_uses}
                onChange={(e) => setNewDiscount({ ...newDiscount, max_uses: parseInt(e.target.value || "0") })}
                data-testid="discount-max-uses"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent" placeholder="Max uses (0=∞)" />
              <select value={newDiscount.applies_to} onChange={(e) => setNewDiscount({ ...newDiscount, applies_to: e.target.value })}
                data-testid="discount-applies"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent">
                <option value="all">All plans</option>
                <option value="starter">Starter only</option>
                <option value="growth">Growth only</option>
                <option value="boost">Boosts only</option>
              </select>
              <input value={newDiscount.note} onChange={(e) => setNewDiscount({ ...newDiscount, note: e.target.value })}
                data-testid="discount-note"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent" placeholder="Note" />
            </div>
            <button onClick={createDiscount} data-testid="create-discount" className="btn-terra mt-4">
              <Plus className="w-4 h-4" /> Create discount
            </button>
          </section>
          <div className="mt-8 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {discounts.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No discount codes yet.</div>}
            {discounts.map((d) => (
              <div key={d.code} data-testid={`discount-${d.code}`} className="bg-[#FDFCF8] p-5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono font-bold text-xl tracking-widest">{d.code}</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">-{d.percent_off}% · {d.applies_to} · {d.uses}/{d.max_uses === 0 ? "∞" : d.max_uses} used · {d.note || "—"}{d.is_revoked && " · REVOKED"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => navigator.clipboard?.writeText(d.code)} className="p-2 hover:text-[var(--brand-primary)]"><Copy className="w-4 h-4" /></button>
                  {!d.is_revoked
                    ? <button onClick={() => revokeDiscount(d.code)} data-testid={`revoke-discount-${d.code}`} className="text-sm font-semibold hover:text-[var(--brand-primary)]">Revoke</button>
                    : <button onClick={() => reinstateDiscount(d.code)} data-testid={`reinstate-discount-${d.code}`} className="text-sm font-semibold text-green-700 hover:underline inline-flex items-center gap-1"><RefreshCcw className="w-3 h-3" /> Reinstate</button>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "reports" && (
        <div className="mt-8">
          <p className="text-sm text-[#5C5C5C] mb-3">Moderation is subject to availability — we review reports as soon as we can.</p>
          <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {reports.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No reports yet.</div>}
            {reports.map((r) => (
              <div key={r.report_id} data-testid={`report-${r.report_id}`} className="bg-[#FDFCF8] p-5 grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 md:col-span-7 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${r.status === "open" || r.status === "reopened" ? "bg-[var(--brand-primary)] text-white" : "bg-[#E2E0D8]"}`}>{r.status}</span>
                    <span className="label-eyebrow">{r.target_type}</span>
                    {(r.notes?.length || 0) > 0 && <span className="text-[10px] text-[#5C5C5C] uppercase tracking-widest">{r.notes.length} notes</span>}
                  </div>
                  <div className="font-display font-bold mt-1">{r.reason}</div>
                  <div className="text-xs text-[#5C5C5C] mt-0.5">{r.detail || "no detail"}</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">Target: <code>{r.target_id}</code> · By: {r.reporter_email} · {new Date(r.created_at).toLocaleString()}</div>
                </div>
                <div className="col-span-12 md:col-span-5 flex gap-2 flex-wrap md:justify-end">
                  <button onClick={() => { setEditingReport(r); setReportNote(""); }} data-testid={`report-open-${r.report_id}`} className="text-sm border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]"><Eye className="w-3.5 h-3.5 inline mr-1" /> Open</button>
                  {(r.status === "open" || r.status === "reopened") && (
                    <>
                      <button onClick={() => resolveReport(r.report_id, "dismiss")} data-testid={`report-dismiss-${r.report_id}`} className="text-sm border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Dismiss</button>
                      {r.target_type === "job" && (
                        <button onClick={() => resolveReport(r.report_id, "remove")} data-testid={`report-remove-${r.report_id}`} className="btn-terra text-sm py-1.5 px-3">Unpublish</button>
                      )}
                      {r.target_type === "user" && (
                        <button onClick={() => resolveReport(r.report_id, "blacklist")} data-testid={`report-blacklist-${r.report_id}`} className="btn-terra text-sm py-1.5 px-3">Blacklist</button>
                      )}
                    </>
                  )}
                  {r.status === "resolved" && (
                    <button onClick={() => reopenReport(r.report_id)} data-testid={`report-reopen-${r.report_id}`} className="text-sm border border-[var(--brand-primary)] text-[var(--brand-primary)] px-3 py-1.5 hover:bg-[var(--brand-primary)] hover:text-white"><RefreshCcw className="w-3.5 h-3.5 inline mr-1" /> Reopen</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "blacklist" && (
        <div className="mt-8">
          <section className="border border-[#0F0F0F] p-6">
            <div className="label-eyebrow">Add to blacklist</div>
            <p className="text-xs text-[#5C5C5C] mt-1">Email (<code>person@example.com</code>) or domain (<code>@example.com</code>).</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <input value={newBlacklist.pattern} onChange={(e) => setNewBlacklist({ ...newBlacklist, pattern: e.target.value })}
                data-testid="blacklist-pattern"
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent" placeholder="@bad-domain.com" />
              <input value={newBlacklist.reason} onChange={(e) => setNewBlacklist({ ...newBlacklist, reason: e.target.value })}
                data-testid="blacklist-reason"
                className="border border-[#0F0F0F] px-3 py-2 sm:col-span-2 bg-transparent" placeholder="Reason" />
            </div>
            <button onClick={addBlacklist} data-testid="add-blacklist" className="btn-terra mt-4"><Ban className="w-4 h-4" /> Add</button>
          </section>
          <div className="mt-8 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {blacklist.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No blacklist entries.</div>}
            {blacklist.map((b) => (
              <div key={b.pattern} className="bg-[#FDFCF8] p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="font-mono font-semibold">{b.pattern}</div>
                  <div className="text-xs text-[#5C5C5C]">{b.reason || "no reason"} · {new Date(b.created_at).toLocaleString()}</div>
                </div>
                <button onClick={() => removeBlacklist(b.pattern)} className="text-sm font-semibold hover:text-[var(--brand-primary)]">Remove</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "jobs" && (
        <div className="mt-8">
          <p className="text-sm text-[#5C5C5C]">Boost any role above the rest. Higher priority + premium tier float to the top of the public board.</p>
          <div className="mt-6 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {jobs.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No jobs yet.</div>}
            {jobs.map((j) => (
              <div key={j.job_id} data-testid={`admin-job-${j.job_id}`} className="bg-[#FDFCF8] p-5 grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-5 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {j.tier === "premium" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-1.5 py-0.5"><Star className="w-2.5 h-2.5 text-[var(--brand-primary)]" /> Spotlight</span>}
                    {j.tier === "featured" && <span className="text-[10px] uppercase tracking-widest font-semibold border border-[var(--brand-primary)] text-[var(--brand-primary)] px-1.5 py-0.5">Featured</span>}
                    {!j.is_published && <span className="text-[10px] uppercase tracking-widest font-semibold bg-[#5C5C5C] text-white px-1.5 py-0.5">Draft</span>}
                  </div>
                  <div className="font-display font-bold mt-1 truncate">{j.title}</div>
                  <div className="text-xs text-[#5C5C5C] truncate">{j.company} · {j.recruiter_name} · priority {j.priority || 0}</div>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <label className="label-eyebrow block mb-1">Tier</label>
                  <select defaultValue={j.tier || "free"} onChange={(e) => prioritise(j.job_id, { tier: e.target.value })}
                    className="w-full border border-[#0F0F0F] bg-transparent px-2 py-1.5 text-sm">
                    <option value="free">Standard</option>
                    <option value="featured">Featured</option>
                    <option value="premium">Premium Spotlight</option>
                  </select>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="label-eyebrow block mb-1">Priority</label>
                  <input type="number" min={0} max={1000} defaultValue={j.priority || 0}
                    onBlur={(e) => prioritise(j.job_id, { priority: parseInt(e.target.value || "0") })}
                    className="w-full border border-[#0F0F0F] bg-transparent px-2 py-1.5 text-sm" />
                </div>
                <div className="col-span-12 md:col-span-2 flex gap-2 justify-end">
                  <Link to={`/recruiter/jobs/${j.job_id}/edit`} className="p-2 hover:text-[var(--brand-primary)]"><Pencil className="w-4 h-4" /></Link>
                  <button onClick={() => prioritise(j.job_id, { is_published: !j.is_published })}
                    className="text-xs font-semibold border border-[#0F0F0F] px-2 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">
                    {j.is_published ? "Unpublish" : "Publish"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!loading && tab === "announcements" && (
        <div className="mt-8" data-testid="tab-content-announcements">
          {isOwner && (
            <section className="border border-[#0F0F0F] p-6">
              <div className="label-eyebrow flex items-center gap-1.5"><Megaphone className="w-3 h-3" /> Publish announcement</div>
              <p className="text-xs text-[#5C5C5C] mt-1">Shown as a banner at the top of every page. Use it for status updates, discount promos and policy changes.</p>
              <div className="grid sm:grid-cols-4 gap-3 mt-3">
                <input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} placeholder="Title" data-testid="ann-title" className="border border-[#0F0F0F] px-3 py-2 bg-transparent sm:col-span-2" />
                <select value={newAnnouncement.kind} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, kind: e.target.value })} data-testid="ann-kind" className="border border-[#0F0F0F] px-3 py-2 bg-transparent">
                  <option value="info">Info</option>
                  <option value="warn">Warning</option>
                  <option value="promo">Promo</option>
                  <option value="discount">Discount</option>
                </select>
                <label className="flex items-center gap-2 text-sm border border-[#E2E0D8] px-3 py-2">
                  <input type="checkbox" checked={newAnnouncement.is_active} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, is_active: e.target.checked })} data-testid="ann-active" />
                  Live
                </label>
              </div>
              <textarea value={newAnnouncement.body} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, body: e.target.value })} placeholder="Body (optional)" data-testid="ann-body" rows={2} className="border border-[#0F0F0F] px-3 py-2 bg-transparent w-full mt-3" />
              <button onClick={createAnnouncement} data-testid="create-ann" className="btn-terra mt-4"><Plus className="w-4 h-4" /> Publish</button>
            </section>
          )}
          <div className="mt-8 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {announcements.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No announcements yet.</div>}
            {announcements.map((a) => (
              <div key={a.announcement_id} data-testid={`ann-${a.announcement_id}`} className="bg-[#FDFCF8] p-5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${a.is_active ? "bg-green-100 text-green-800" : "bg-[#E2E0D8]"}`}>{a.is_active ? "Live" : "Draft"}</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border border-[#0F0F0F]">{a.kind}</span>
                  </div>
                  <div className="font-display font-bold mt-1">{a.title}</div>
                  {a.body && <div className="text-sm text-[#5C5C5C] mt-0.5">{a.body}</div>}
                  <div className="text-xs text-[#5C5C5C] mt-2">By {a.created_by_email} · {new Date(a.created_at).toLocaleString()}</div>
                </div>
                {isOwner && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => toggleAnnouncement(a)} data-testid={`toggle-ann-${a.announcement_id}`} className="text-xs font-semibold border border-[#0F0F0F] px-2 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">
                      {a.is_active ? "Pause" : "Resume"}
                    </button>
                    <button onClick={() => deleteAnnouncement(a.announcement_id)} data-testid={`delete-ann-${a.announcement_id}`} className="p-2 hover:text-[var(--brand-primary)]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "appeals" && (
        <div className="mt-8" data-testid="tab-content-appeals">
          <p className="text-sm text-[#5C5C5C] mb-3">Users with active bans can submit one appeal. Reversing an appeal also reinstates the user.</p>
          <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {appeals.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No appeals.</div>}
            {appeals.map((a) => (
              <div key={a.appeal_id} data-testid={`appeal-${a.appeal_id}`} className="bg-[#FDFCF8] p-5 grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 md:col-span-8 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${a.status === "open" ? "bg-[var(--brand-primary)] text-white" : "bg-[#E2E0D8]"}`}>{a.status}{a.action ? ` · ${a.action}` : ""}</span>
                  </div>
                  <div className="font-display font-bold mt-1">{a.user_email}</div>
                  <div className="text-xs text-[#5C5C5C] mt-0.5">Original ban reason: {a.ban_reason || "—"}</div>
                  <p className="text-sm mt-2 whitespace-pre-wrap">{a.message}</p>
                  <div className="text-xs text-[#5C5C5C] mt-1">Filed {new Date(a.created_at).toLocaleString()}</div>
                  {a.resolution_note && <div className="text-xs text-[#5C5C5C] mt-2 italic">Resolution: {a.resolution_note}</div>}
                </div>
                {a.status === "open" && (
                  <div className="col-span-12 md:col-span-4 flex gap-2 flex-wrap md:justify-end">
                    <button onClick={() => resolveAppeal(a.appeal_id, "reverse")} data-testid={`appeal-reverse-${a.appeal_id}`} className="btn-terra text-sm py-1.5 px-3">Reverse & reinstate</button>
                    <button onClick={() => resolveAppeal(a.appeal_id, "uphold")} data-testid={`appeal-uphold-${a.appeal_id}`} className="text-sm border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Uphold ban</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "moderators" && isOwner && (
        <div className="mt-8" data-testid="tab-content-moderators">
          <p className="text-sm text-[#5C5C5C] mb-4">Promote trusted users to <strong>moderator</strong>. Moderators can handle reports, warn/ban/email users, manage blacklist and appeals. They <strong>cannot</strong> manage billing, discounts, invites, listings, or other moderators.</p>
          <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            <div className="bg-[#FDFCF8] p-4 grid grid-cols-12 gap-3 items-center">
              <div className="col-span-12 md:col-span-8 min-w-0">
                <div className="label-eyebrow">Current moderators</div>
              </div>
            </div>
            {allUsers.filter((u) => u.role === "moderator").length === 0 && (
              <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C] text-sm">No moderators yet. Promote a user below.</div>
            )}
            {allUsers.filter((u) => u.role === "moderator").map((u) => (
              <div key={u.user_id} data-testid={`mod-${u.user_id}`} className="bg-[#FDFCF8] p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{u.name || u.email}</div>
                  <div className="text-sm text-[#5C5C5C] truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="label-eyebrow text-[10px]">Demote to</label>
                  <select
                    data-testid={`demote-select-${u.user_id}`}
                    defaultValue=""
                    onChange={(e) => {
                      const nr = e.target.value;
                      if (nr) {
                        changeRole(u.user_id, nr);
                        e.target.value = "";
                      }
                    }}
                    className="text-xs border border-[#0F0F0F] bg-transparent px-2 py-1.5"
                  >
                    <option value="" disabled>choose role…</option>
                    <option value="candidate">candidate</option>
                    <option value="recruiter">recruiter</option>
                  </select>
                </div>
              </div>
            ))}
            <div className="bg-[#FDFCF8] p-4 grid grid-cols-12 gap-3 items-center">
              <div className="col-span-12 md:col-span-8 min-w-0">
                <div className="label-eyebrow">Promote a user</div>
                <div className="text-xs text-[#5C5C5C] mt-1">Search any non-owner user and click <em>Make moderator</em>.</div>
              </div>
            </div>
            {allUsers.filter((u) => u.role !== "owner" && u.role !== "moderator").slice(0, 20).map((u) => (
              <div key={u.user_id} className="bg-[#FDFCF8] p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{u.name || u.email} <span className="text-xs text-[#5C5C5C] ml-2">{u.role}</span></div>
                  <div className="text-xs text-[#5C5C5C] truncate">{u.email}</div>
                </div>
                <button onClick={() => changeRole(u.user_id, "moderator")} data-testid={`promote-${u.user_id}`} className="btn-terra text-sm py-1.5 px-3">
                  <ShieldCheck className="w-3.5 h-3.5" /> Make moderator
                </button>
              </div>
            ))}
            {allUsers.filter((u) => u.role !== "owner" && u.role !== "moderator").length > 20 && (
              <div className="bg-[#FDFCF8] p-4 text-xs text-[#5C5C5C]">Showing first 20 — use Users tab to find specific accounts.</div>
            )}
          </div>
        </div>
      )}

      {!loading && tab === "myplan" && isOwner && (
        <div className="mt-8 max-w-xl" data-testid="tab-content-myplan">
          <div className="label-eyebrow flex items-center gap-1.5"><Crown className="w-3 h-3 text-[var(--brand-primary)]" /> Owner plan</div>
          <h2 className="font-display font-black text-3xl mt-3">Your plan, your label.</h2>
          <p className="text-sm text-[#5C5C5C] mt-2">As the owner you have unlimited access — pick a label for it. Shown as a badge in the header.</p>
          <div className="mt-5 space-y-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Label</label>
              <input data-testid="ownerplan-label" value={ownerPlan.label} onChange={(e) => setOwnerPlan({ ...ownerPlan, label: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" placeholder="Owner Unlimited" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Note (private)</label>
              <textarea data-testid="ownerplan-note" value={ownerPlan.note} onChange={(e) => setOwnerPlan({ ...ownerPlan, note: e.target.value })} rows={2} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
            </div>
            <button onClick={saveOwnerPlan} data-testid="save-ownerplan" className="btn-terra"><Crown className="w-4 h-4" /> Save plan</button>
          </div>
        </div>
      )}

      {/* User detail modal */}
      {detailUser && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" data-testid="user-detail-modal" onClick={() => setDetailUser(null)}>
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-eyebrow">User detail</div>
                <h3 className="font-display font-black text-2xl mt-1">{detailUser.name || detailUser.email}</h3>
                <div className="text-sm text-[#5C5C5C]">{detailUser.email} · {detailUser.role}{detailUser.is_banned ? " · BANNED" : ""}</div>
              </div>
              <button onClick={() => setDetailUser(null)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div><div className="label-eyebrow">Auth</div><div>{detailUser.auth_provider || "local"}</div></div>
              <div><div className="label-eyebrow">2FA</div><div>{detailUser.two_factor_enabled ? "On" : "Off"}</div></div>
              <div><div className="label-eyebrow">Warnings</div><div>{detailUser.warnings_count || 0}</div></div>
              <div><div className="label-eyebrow">Applications</div><div>{detailUser.applications_count}</div></div>
              <div><div className="label-eyebrow">Jobs posted</div><div>{detailUser.jobs_count}</div></div>
              <div><div className="label-eyebrow">Approved</div><div>{detailUser.is_approved ? "Yes" : "No"}</div></div>
              <div><div className="label-eyebrow">Phone</div><div>{detailUser.phone || "—"}</div></div>
              <div><div className="label-eyebrow">LinkedIn</div><div className="truncate">{detailUser.linkedin_url || "—"}</div></div>
              <div><div className="label-eyebrow">Company</div><div>{detailUser.company_name || "—"}</div></div>
              <div><div className="label-eyebrow">Created</div><div>{detailUser.created_at ? new Date(detailUser.created_at).toLocaleString() : "—"}</div></div>
              <div className="col-span-2"><div className="label-eyebrow">Last login</div><div>{detailUser.last_login_at ? `${new Date(detailUser.last_login_at).toLocaleString()} · ${detailUser.last_login_ip || "—"}` : "Never"}</div></div>
            </div>
            {detailUser.is_banned && (
              <div className="mt-4 border border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 p-3 text-sm">
                <div className="label-eyebrow text-[var(--brand-primary)]">Ban</div>
                <div className="mt-1">{detailUser.ban_reason || "—"}</div>
                {detailUser.ban_note && <div className="text-xs text-[#5C5C5C] mt-1">{detailUser.ban_note}</div>}
              </div>
            )}
            {detailUser.warnings?.length > 0 && (
              <div className="mt-4">
                <div className="label-eyebrow">Warnings</div>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {detailUser.warnings.slice().reverse().map((w) => (
                    <div key={w.id} className="border border-[#E2E0D8] p-2 text-xs">
                      <strong>{w.reason}</strong> <span className="text-[#5C5C5C]">— {new Date(w.at).toLocaleString()} by {w.issued_by_email}</span>
                      {w.note && <div className="mt-1">{w.note}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detailUser.login_history?.length > 0 && (
              <div className="mt-4">
                <div className="label-eyebrow">Login history (last 20)</div>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto text-xs font-mono">
                  {detailUser.login_history.slice().reverse().map((h, i) => (
                    <div key={`${h.at}-${i}`} className="text-[#5C5C5C]">{new Date(h.at).toLocaleString()} · {h.ip || "?"} · {(h.ua || "").slice(0, 60)}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action modal (warn / ban / email) */}
      {actionTarget && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" data-testid="action-modal" onClick={() => setActionTarget(null)}>
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5">
              {actionTarget.kind === "warn" && <><AlertTriangle className="w-3 h-3 text-amber-600" /> Warn user</>}
              {actionTarget.kind === "limit" && <><AlertCircle className="w-3 h-3 text-amber-600" /> Limit user</>}
              {actionTarget.kind === "ban" && <><Ban className="w-3 h-3 text-[var(--brand-primary)]" /> Ban user</>}
              {actionTarget.kind === "deny" && <><ShieldOff className="w-3 h-3 text-[var(--brand-primary)]" /> Deny recruiter</>}
              {actionTarget.kind === "email" && <><Mail className="w-3 h-3" /> Email user</>}
            </div>
            <h3 className="font-display font-black text-2xl mt-2">{actionTarget.user.name || actionTarget.user.email}</h3>
            <div className="text-sm text-[#5C5C5C]">{actionTarget.user.email} · {actionTarget.user.role}</div>

            {(actionTarget.kind === "warn" || actionTarget.kind === "ban" || actionTarget.kind === "limit" || actionTarget.kind === "deny") && (
              <div className="mt-5 space-y-3">
                <div>
                  <label className="label-eyebrow block mb-1.5">Reason *</label>
                  <input data-testid="action-reason" required value={actionForm.reason} onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" placeholder={
                    actionTarget.kind === "ban" ? "Why ban this user?" :
                    actionTarget.kind === "limit" ? "Why limit this user?" :
                    actionTarget.kind === "deny" ? "Why deny this recruiter?" :
                    "Why warn this user?"
                  } />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1.5">Note (visible to mods)</label>
                  <textarea data-testid="action-note" rows={2} value={actionForm.note} onChange={(e) => setActionForm({ ...actionForm, note: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={
                    actionTarget.kind === "warn" ? warnUser :
                    actionTarget.kind === "limit" ? limitUser :
                    actionTarget.kind === "deny" ? denyRecruiter :
                    banUser
                  } disabled={!actionForm.reason.trim()} data-testid={`confirm-${actionTarget.kind}`} className={actionTarget.kind === "ban" || actionTarget.kind === "deny" ? "btn-terra flex-1" : "btn-ink flex-1"}>
                    {actionTarget.kind === "warn" && <><AlertTriangle className="w-4 h-4" /> Issue warning</>}
                    {actionTarget.kind === "limit" && <><AlertCircle className="w-4 h-4" /> Limit user</>}
                    {actionTarget.kind === "ban" && <><Ban className="w-4 h-4" /> Ban user</>}
                    {actionTarget.kind === "deny" && <><ShieldOff className="w-4 h-4" /> Deny recruiter</>}
                  </button>
                  <button onClick={() => setActionTarget(null)} className="btn-ghost">Cancel</button>
                </div>
                {actionTarget.kind === "ban" && (
                  <p className="text-xs text-[#5C5C5C] italic">Banning revokes active sessions immediately. The user can submit one appeal via /appeal.</p>
                )}
                {actionTarget.kind === "limit" && (
                  <p className="text-xs text-[#5C5C5C] italic">Limited users can log in and browse but cannot post jobs or submit applications.</p>
                )}
                {actionTarget.kind === "deny" && (
                  <p className="text-xs text-[#5C5C5C] italic">Denied recruiters see a banner explaining the denial and can submit a review request.</p>
                )}
              </div>
            )}

            {actionTarget.kind === "email" && (
              <div className="mt-5 space-y-3">
                <div>
                  <label className="label-eyebrow block mb-1.5">Subject *</label>
                  <input data-testid="email-subject" required value={actionForm.subject} onChange={(e) => setActionForm({ ...actionForm, subject: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-eyebrow block mb-1.5">CC <span className="opacity-50 normal-case">(optional)</span></label>
                    <input data-testid="email-cc" value={actionForm.cc || ""} onChange={(e) => setActionForm({ ...actionForm, cc: e.target.value })} placeholder="ops@acme.com, hr@acme.com" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1.5">BCC <span className="opacity-50 normal-case">(optional)</span></label>
                    <input data-testid="email-bcc" value={actionForm.bcc || ""} onChange={(e) => setActionForm({ ...actionForm, bcc: e.target.value })} placeholder="archive@acme.com" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-sm" />
                  </div>
                </div>
                <p className="text-[10px] text-[#5C5C5C]">Separate multiple addresses with commas. Invalid addresses are dropped silently.</p>
                <div>
                  <label className="label-eyebrow block mb-1.5">Body *</label>
                  <textarea data-testid="email-body" required rows={5} value={actionForm.body} onChange={(e) => setActionForm({ ...actionForm, body: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
                </div>
                <p className="text-xs text-[#5C5C5C] italic">Delivered via Resend from noreply@oceanash.org. The recipient will be able to reply directly to admin@oceanash.org.</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={emailUser} disabled={!actionForm.subject.trim() || !actionForm.body.trim()} data-testid="confirm-email" className="btn-terra flex-1">
                    <Mail className="w-4 h-4" /> Send email
                  </button>
                  <button onClick={() => setActionTarget(null)} className="btn-ghost">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Companies tab */}
      {!loading && tab === "companies" && (
        <div className="mt-8" data-testid="tab-content-companies">
          <div className="flex items-start justify-between gap-3 mb-4">
            <p className="text-sm text-[#5C5C5C]">All companies on the platform. Recruiters auto-create a company on signup; you can also pre-seed a company here (no recruiter attached) and assign a user to it later via the Edit User modal.</p>
            {isOwner && (
              <button
                data-testid="open-new-company-form"
                onClick={() => { setNewCompanyOpen((v) => !v); setNewCompanyError(""); }}
                className="btn-terra text-sm whitespace-nowrap"
              >
                <Plus className="w-4 h-4" /> {newCompanyOpen ? "Cancel" : "New company"}
              </button>
            )}
          </div>
          {newCompanyOpen && isOwner && (
            <div data-testid="new-company-form" className="mb-4 border border-[#0F0F0F] p-4 bg-[#0F0F0F]/5 grid gap-3 md:grid-cols-2">
              <div>
                <label className="label-eyebrow block mb-1.5">Company name</label>
                <input
                  data-testid="new-company-name"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                  placeholder="Acme Inc"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2"
                  autoFocus
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Website (optional)</label>
                <input
                  data-testid="new-company-website"
                  value={newCompany.website}
                  onChange={(e) => setNewCompany({ ...newCompany, website: e.target.value })}
                  placeholder="https://acme.com"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono text-xs"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Location (optional)</label>
                <input
                  data-testid="new-company-location"
                  value={newCompany.location}
                  onChange={(e) => setNewCompany({ ...newCompany, location: e.target.value })}
                  placeholder="London, UK"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-eyebrow block mb-1.5">Description (optional)</label>
                <textarea
                  data-testid="new-company-description"
                  value={newCompany.description}
                  onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                  rows={2}
                  placeholder="What does this company do?"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2"
                />
              </div>
              <div className="md:col-span-2">
                <div className="label-eyebrow block mb-1.5">Organisation type</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "business", t: "Real business" },
                    { v: "game", t: "Game group · disclaimer on listings" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      data-testid={`new-company-orgtype-${opt.v}`}
                      onClick={() => setNewCompany({ ...newCompany, org_type: opt.v })}
                      className={`text-sm border px-3 py-2 text-left transition ${newCompany.org_type === opt.v ? "border-[#0F0F0F] bg-[#0F0F0F] text-[#FDFCF8]" : "border-[#E2E0D8] hover:border-[#0F0F0F]"}`}
                    >
                      {opt.t}
                    </button>
                  ))}
                </div>
              </div>
              {newCompanyError && <div data-testid="new-company-error" className="md:col-span-2 text-sm text-[var(--brand-primary)]">{newCompanyError}</div>}
              <div className="md:col-span-2 flex gap-2">
                <button
                  onClick={createCompany}
                  disabled={newCompanySaving || !newCompany.name.trim()}
                  data-testid="save-new-company"
                  className="btn-terra disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" /> {newCompanySaving ? "Creating…" : "Create company"}
                </button>
                <button
                  onClick={() => { setNewCompanyOpen(false); setNewCompanyError(""); }}
                  className="btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {companies.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No companies yet.</div>}
            {companies.map((c) => (
              <div key={c.company_id} data-testid={`company-${c.company_id}`} className="bg-[#FDFCF8] p-4 grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 md:col-span-7 min-w-0">
                  <div className="font-display font-bold truncate">{c.name}{c.created_by_admin && !c.owner_user_id && <span className="ml-2 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-900">Unassigned</span>}{(c.org_type === "game") && <span data-testid={`company-orgtype-game-${c.company_id}`} className="ml-2 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-purple-100 text-purple-900">Game group</span>}</div>
                  <div className="text-xs text-[#5C5C5C] mt-1 font-mono">{c.company_id}</div>
                  <div className="text-sm text-[#5C5C5C] mt-1">{c.location || "—"} · {c.members?.length || 0} member{(c.members?.length || 0) === 1 ? "" : "s"}</div>
                  {c.website && <div className="text-xs text-[#5C5C5C] mt-1"><a href={c.website} target="_blank" rel="noreferrer" className="underline hover:text-[var(--brand-primary)]">{c.website}</a></div>}
                </div>
                <div className="col-span-12 md:col-span-5 flex justify-end gap-2 flex-wrap">
                  <button onClick={() => setEditingCompany({ ...c })} data-testid={`edit-company-${c.company_id}`} className="text-sm border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]"><Pencil className="w-3.5 h-3.5 inline mr-1" /> Edit</button>
                  {isOwner && (
                    <button onClick={() => deleteCompany(c.company_id)} data-testid={`delete-company-${c.company_id}`} className="text-sm hover:text-[var(--brand-primary)] px-3 py-1.5"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recruiter review requests */}
      {!loading && tab === "reviews" && (
        <div className="mt-8" data-testid="tab-content-reviews">
          <p className="text-sm text-[#5C5C5C] mb-3">Denied recruiters can request a review. Reverse to approve them; uphold to keep the denial.</p>
          <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {reviews.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No review requests.</div>}
            {reviews.map((rv) => (
              <div key={rv.review_id} data-testid={`review-${rv.review_id}`} className="bg-[#FDFCF8] p-5 grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 md:col-span-8 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${rv.status === "open" ? "bg-[var(--brand-primary)] text-white" : "bg-[#E2E0D8]"}`}>{rv.status}{rv.action ? ` · ${rv.action}` : ""}</span>
                  </div>
                  <div className="font-display font-bold mt-1">{rv.user_email}</div>
                  <div className="text-xs text-[#5C5C5C] mt-0.5">Company: {rv.company_name || "—"} · Original denial: {rv.denial_reason || "—"}</div>
                  <p className="text-sm mt-2 whitespace-pre-wrap">{rv.message}</p>
                  <div className="text-xs text-[#5C5C5C] mt-1">Filed {new Date(rv.created_at).toLocaleString()}</div>
                </div>
                {rv.status === "open" && isOwner && (
                  <div className="col-span-12 md:col-span-4 flex gap-2 flex-wrap md:justify-end">
                    <button onClick={() => resolveReview(rv.review_id, "reverse")} data-testid={`review-reverse-${rv.review_id}`} className="btn-terra text-sm py-1.5 px-3">Reverse & approve</button>
                    <button onClick={() => resolveReview(rv.review_id, "uphold")} data-testid={`review-uphold-${rv.review_id}`} className="text-sm border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Uphold denial</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signed 12-Month Contracts Tab */}
      {!loading && tab === "contracts" && (
        <div className="mt-8" data-testid="tab-content-contracts">
          <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
            <p className="text-sm text-[#5C5C5C]">View and inspect all active 12-Month Contracts signed by recruiters on the platform.</p>
            <button
              onClick={() => {
                setShowIssueCustomContract(!showIssueCustomContract);
                if (contractTemplateText && !customContractForm.contract_text) {
                  setCustomContractForm({ ...customContractForm, contract_text: contractTemplateText });
                }
              }}
              data-testid="toggle-custom-contract-btn"
              className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 bg-[var(--brand-primary)] text-white hover:bg-[#B83A28] transition"
            >
              {showIssueCustomContract ? "Hide Issuance Form" : "✍️ Issue Ad-hoc Custom Contract"}
            </button>
          </div>
          
          {/* Issue Custom Contract Form */}
          {showIssueCustomContract && (
            <form onSubmit={handleIssueCustomContract} className="mb-6 border border-[#E2E0D8] p-5 bg-[#F3F2EB] space-y-4 text-left">
              <h3 className="font-display font-bold text-lg text-[#0F0F0F]">Issue Ad-hoc Custom Contract (Owner Only)</h3>
              <p className="text-xs text-[#5C5C5C]">Creates a binding 12-month contract commitment for any User ID with customized price, plan tier, and legal text.</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">User ID *</label>
                  <input required data-testid="issue-contract-user-id" type="text" value={customContractForm.user_id} onChange={(e) => setCustomContractForm({ ...customContractForm, user_id: e.target.value })} placeholder="e.g. user_abc123" className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Plan Tier Name *</label>
                  <input required data-testid="issue-contract-tier" type="text" value={customContractForm.tier} onChange={(e) => setCustomContractForm({ ...customContractForm, tier: e.target.value })} placeholder="e.g. growth" className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Monthly Price (Pence) *</label>
                  <input required data-testid="issue-contract-price" type="number" min={0} value={customContractForm.monthly_price_pence} onChange={(e) => setCustomContractForm({ ...customContractForm, monthly_price_pence: e.target.value })} className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Discount % (0-100)</label>
                  <input data-testid="issue-contract-discount" type="number" min={0} max={100} value={customContractForm.discount_percent} onChange={(e) => setCustomContractForm({ ...customContractForm, discount_percent: e.target.value })} className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Contract Start Date *</label>
                  <input required data-testid="issue-contract-start-date" type="date" value={customContractForm.start_date} onChange={(e) => setCustomContractForm({ ...customContractForm, start_date: e.target.value })} className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Length in Months *</label>
                  <input required data-testid="issue-contract-length" type="number" min={1} max={120} value={customContractForm.length_months} onChange={(e) => setCustomContractForm({ ...customContractForm, length_months: e.target.value })} className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Calculated End Date</label>
                  <div className="w-full border border-[#E2E0D8] bg-[#F3F2EB] px-3 py-2 text-sm text-[#5C5C5C] font-mono">
                    {(() => {
                      if (!customContractForm.start_date || !customContractForm.length_months) return "—";
                      try {
                        const parts = customContractForm.start_date.split("-");
                        const start = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
                        const length = parseInt(customContractForm.length_months, 10) || 0;
                        start.setMonth(start.getMonth() + length);
                        return start.toISOString().split("T")[0];
                      } catch (e) {
                        return "—";
                      }
                    })()}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Contract Agreement Text *</label>
                <textarea
                  required
                  data-testid="issue-contract-text"
                  value={customContractForm.contract_text}
                  onChange={(e) => setCustomContractForm({ ...customContractForm, contract_text: e.target.value })}
                  rows={8}
                  className="w-full border border-[#0F0F0F] bg-white p-3 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F]"
                  placeholder="Paste bespoke agreement details here..."
                />
              </div>

              <button
                type="submit"
                disabled={issuingCustomContract}
                data-testid="submit-issue-contract-btn"
                className="btn-terra w-full uppercase text-xs font-semibold tracking-wider py-2"
              >
                {issuingCustomContract ? "Issuing Contract..." : "Create & Issue bespoke contract"}
              </button>
            </form>
          )}

          {/* Customise Contract Template Accordion */}
          <div className="mb-6 border border-[#E2E0D8] p-4 bg-[#F3F2EB]">
            <button
              onClick={() => setShowTemplateEditor(!showTemplateEditor)}
              data-testid="toggle-template-editor"
              className="font-display font-bold text-sm flex items-center gap-1.5 text-[#0F0F0F] hover:text-[var(--brand-primary)]"
            >
              ⚙️ {showTemplateEditor ? "Hide Contract Template Editor" : "Customise Default Contract Template"}
            </button>
            {showTemplateEditor && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-[#5C5C5C]">
                  Edit the default legal text of recruiter agreements. Use the following placeholders which will be automatically substituted when generating contracts:
                  <br />
                  <code>{"{{company_name}}"}</code>, <code>{"{{signer_name}}"}</code>, <code>{"{{tier}}"}</code>, <code>{"{{monthly_price}}"}</code>, <code>{"{{total_price}}"}</code>, <code>{"{{signed_at}}"}</code>, <code>{"{{effective_date}}"}</code>, <code>{"{{end_date}}"}</code>, <code>{"{{dob}}"}</code>, <code>{"{{normal_price}}"}</code>, <code>{"{{discounted_monthly}}"}</code>, <code>{"{{discounted_total}}"}</code>.
                </p>
                <textarea
                  value={contractTemplateText}
                  onChange={(e) => setContractTemplateText(e.target.value)}
                  data-testid="contract-template-input"
                  rows={10}
                  className="w-full border border-[#0F0F0F] bg-white p-3 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F]"
                  placeholder="Paste master agreement text here..."
                />
                <button
                  onClick={handleSaveContractTemplate}
                  disabled={savingTemplate}
                  data-testid="save-contract-template-btn"
                  className="bg-[var(--brand-primary)] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 hover:bg-[#B83A28] disabled:opacity-40 transition"
                >
                  {savingTemplate ? "Saving template..." : "Save Contract Template"}
                </button>
              </div>
            )}
          </div>

          <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {adminContracts.length === 0 && <div className="bg-[#FDFCF8] p-6 text-[#5C5C5C]">No contracts signed yet.</div>}
            {adminContracts.map((ct) => (
              <div key={ct.contract_id} data-testid={`admin-contract-${ct.contract_id}`} className="bg-[#FDFCF8] p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${ct.status === "signed" ? "bg-green-700 text-white" : "bg-red-700 text-white"}`}>{ct.status}</span>
                    <span className="font-mono text-xs text-[#5C5C5C]">{ct.contract_id}</span>
                  </div>
                  <div className="font-display font-bold text-lg mt-1 capitalize">{ct.tier} Plan — 12-Month Contract</div>
                  <div className="text-xs text-[#5C5C5C] mt-0.5">
                    Subscriber: <strong>{ct.customer_name || "—"}</strong> ({ct.customer_email || "—"})
                  </div>
                  {ct.company_name && (
                    <div className="text-xs text-[#5C5C5C]">
                      Company: <strong>{ct.company_name}</strong>
                    </div>
                  )}
                  <div className="text-xs text-[#5C5C5C] mt-1">
                    Signed by <strong>{ct.signed_by_name}</strong> {ct.signer_title ? `(${ct.signer_title})` : ""} on {new Date(ct.signed_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-[#5C5C5C]">
                    Term: {new Date(ct.start_date).toLocaleDateString()} to {new Date(ct.end_date).toLocaleDateString()} · £{(ct.monthly_price_pence/100).toFixed(2)}/mo · Total: £{(ct.total_price_pence/100).toFixed(2)}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap sm:flex-col sm:items-end">
                  <button
                    onClick={() => setAdminSelectedContract(ct)}
                    data-testid={`admin-view-contract-${ct.contract_id}`}
                    className="text-xs font-semibold uppercase tracking-wider py-1 px-3 border border-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-[#FDFCF8] transition bg-white"
                  >
                    View Agreement
                  </button>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/recruiter/contracts/${ct.contract_id}/pdf`}
                    data-testid={`admin-download-contract-${ct.contract_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold uppercase tracking-wider py-1 px-3 border border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white transition bg-white flex items-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </a>
                  {ct.status === "signed" && (
                    <>
                      <button
                        onClick={() => handleOpenEditTerm(ct)}
                        data-testid={`admin-edit-term-${ct.contract_id}`}
                        className="text-xs font-semibold uppercase tracking-wider py-1 px-3 border border-amber-600 hover:bg-amber-600 hover:text-white text-amber-700 transition bg-white"
                      >
                        ✏️ Edit Term
                      </button>
                      <button
                        onClick={() => handleVoidContract(ct.contract_id)}
                        data-testid={`admin-void-contract-${ct.contract_id}`}
                        className="text-xs font-semibold uppercase tracking-wider py-1 px-3 border border-red-600 hover:bg-red-600 hover:text-white text-red-700 transition bg-white"
                      >
                        🔴 Void Contract
                      </button>
                    </>
                  )}
                  {ct.status === "void" && (
                    <button
                      onClick={() => handleReinstateContract(ct.contract_id)}
                      data-testid={`admin-reinstate-contract-${ct.contract_id}`}
                      className="text-xs font-semibold uppercase tracking-wider py-1 px-3 border border-green-600 hover:bg-green-600 hover:text-white text-green-700 transition bg-white"
                    >
                      🟢 Reinstate Contract
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Tracking Tab */}
      {!loading && tab === "trackers" && (
        <div className="mt-8" data-testid="tab-content-trackers">
          <p className="text-sm text-[#5C5C5C] mb-3">Create and manage customized trackers with live updates and statuses (e.g. Recruiter Account Review, Pay Status, Verification checks).</p>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create tracker form */}
            <div className="lg:col-span-1 border border-[#E2E0D8] p-5 bg-[#F3F2EB]">
              <h3 className="font-display font-bold text-lg mb-3">New Custom Tracker</h3>
              <form onSubmit={handleCreateTracker} className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Target Account ID (User ID or Company ID) *</label>
                  <input data-testid="tracker-target-id" required type="text" value={newTracker.target_id} onChange={(e) => setNewTracker({ ...newTracker, target_id: e.target.value })} placeholder="e.g. user_123 or co_abc" className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Target Type</label>
                  <select data-testid="tracker-target-type" value={newTracker.target_type} onChange={(e) => setNewTracker({ ...newTracker, target_type: e.target.value })} className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]">
                    <option value="recruiter">Recruiter (Individual)</option>
                    <option value="company">Company Tracking (Growth+/Enterprise)</option>
                    <option value="candidate">Candidate</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Tracker Title / Name *</label>
                  <input data-testid="tracker-title" required type="text" value={newTracker.title} onChange={(e) => setNewTracker({ ...newTracker, title: e.target.value })} placeholder="e.g. Recruiter Account Review" className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Status / Custom Status *</label>
                  <input data-testid="tracker-status" required type="text" value={newTracker.status} onChange={(e) => setNewTracker({ ...newTracker, status: e.target.value })} placeholder="e.g. Pending, Unpaid, Active" className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Status Badge Color</label>
                  <select
                    value={newTracker.status_color}
                    onChange={(e) => setNewTracker({ ...newTracker, status_color: e.target.value })}
                    className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]"
                    data-testid="tracker-status-color"
                  >
                    <option value="#5C5C5C">Default (Gray)</option>
                    <option value="#D94833">Red (Terra)</option>
                    <option value="#10B981">Green</option>
                    <option value="#3B82F6">Blue</option>
                    <option value="#F97316">Orange</option>
                    <option value="#8B5CF6">Purple</option>
                    <option value="#EAB308">Yellow</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Tracking Code (Optional)</label>
                  <input data-testid="tracker-code" type="text" value={newTracker.tracking_code} onChange={(e) => setNewTracker({ ...newTracker, tracking_code: e.target.value })} placeholder="e.g. TRK-ABC-123" className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]" />
                </div>
                <button type="submit" disabled={creatingTracker} data-testid="submit-tracker" className="btn-terra w-full uppercase text-xs font-semibold tracking-wider py-2">
                  {creatingTracker ? "Initializing..." : "Create Tracker"}
                </button>
              </form>
            </div>

            {/* List trackers */}
            <div className="lg:col-span-2 space-y-3">
              <h3 className="font-display font-bold text-lg mb-1">All Platform Trackers ({trackers.length})</h3>
              {trackers.length === 0 && <div className="border border-[#E2E0D8] bg-[#FDFCF8] p-6 text-[#5C5C5C]">No trackers configured yet.</div>}
              {trackers.map((tr) => (
                <div key={tr.tracker_id} data-testid={`admin-tracker-${tr.tracker_id}`} className="border border-[#E2E0D8] bg-[#FDFCF8] p-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-white px-1.5 py-0.5" style={{ backgroundColor: tr.status_colors?.[tr.status] || '#D94833' }}>{tr.status}</span>
                      <span className="font-mono text-xs text-[#5C5C5C]">{tr.tracking_code}</span>
                    </div>
                    <div className="font-display font-bold text-lg mt-1">{tr.title}</div>
                    <div className="text-xs text-[#5C5C5C]">
                      Target Account: <code className="bg-[#F3F2EB] px-1 py-0.5 rounded text-[10px]">{tr.target_id}</code> ({tr.target_type})
                    </div>
                    <div className="text-xs text-[#5C5C5C] mt-1">
                      Created on {new Date(tr.created_at).toLocaleDateString()} · {tr.updates?.length || 0} history logs
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      onClick={() => setTrackerModal(tr)}
                      data-testid={`admin-update-tracker-btn-${tr.tracker_id}`}
                      className="text-xs font-semibold uppercase tracking-wider py-1 px-3 border border-[#0F0F0F] bg-white text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-white transition"
                    >
                      Update / History
                    </button>
                    <button
                      onClick={() => handleDeleteTracker(tr.tracker_id)}
                      data-testid={`admin-delete-tracker-btn-${tr.tracker_id}`}
                      className="text-xs font-semibold uppercase tracking-wider py-1 px-2 border border-red-600 text-red-700 hover:bg-red-600 hover:text-white transition"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tracker Updates Modal */}
      {trackerModal && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4" data-testid="tracker-update-modal">
          <div className="bg-[#FDFCF8] border-2 border-[#0F0F0F] max-w-2xl w-full p-6 text-left relative max-h-[90vh] flex flex-col">
            <button onClick={() => setTrackerModal(null)} className="absolute top-4 right-4 text-[#5C5C5C] hover:text-[#0F0F0F]"><X className="w-5 h-5" /></button>
            <div className="label-eyebrow text-amber-700 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Tracker Maintenance</div>
            <h3 className="font-display font-black text-2xl mt-1">{trackerModal.title}</h3>
            <p className="text-xs text-[#5C5C5C] mt-0.5">Tracking Code: <code className="font-mono">{trackerModal.tracking_code}</code> · Current Status: <strong>{trackerModal.status}</strong></p>
            
            {/* New Update Form */}
            <form onSubmit={handleAddTrackerUpdate} className="mt-4 border border-[#E2E0D8] p-4 bg-[#F3F2EB] space-y-3">
              <div className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C]">Post Progress Update / Change Status</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">New Status *</label>
                  <input data-testid="update-status-input" required type="text" value={newUpdate.status} onChange={(e) => setNewUpdate({ ...newUpdate, status: e.target.value })} placeholder="e.g. Under Review, Completed" className="w-full border border-[#0F0F0F] bg-white px-3 py-1 text-sm text-[#0F0F0F]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Status Badge Color</label>
                  <select
                    value={newUpdate.status_color}
                    onChange={(e) => setNewUpdate({ ...newUpdate, status_color: e.target.value })}
                    className="w-full border border-[#0F0F0F] bg-white px-3 py-1 text-sm text-[#0F0F0F]"
                    data-testid="update-status-color"
                  >
                    <option value="#5C5C5C">Gray</option>
                    <option value="#D94833">Red (Terra)</option>
                    <option value="#10B981">Green</option>
                    <option value="#3B82F6">Blue</option>
                    <option value="#F97316">Orange</option>
                    <option value="#8B5CF6">Purple</option>
                    <option value="#EAB308">Yellow</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">Progress Message *</label>
                  <input data-testid="update-message-input" required type="text" value={newUpdate.message} onChange={(e) => setNewUpdate({ ...newUpdate, message: e.target.value })} placeholder="e.g. We are now reviewing it." className="w-full border border-[#0F0F0F] bg-white px-3 py-1 text-sm text-[#0F0F0F]" />
                </div>
              </div>
              <button type="submit" disabled={addingUpdate} data-testid="submit-update-btn" className="btn-terra text-xs py-1.5 w-full">
                {addingUpdate ? "Posting..." : "Post Progress Update"}
              </button>
            </form>

            {/* History Logs list */}
            <div className="mt-4 flex-1 overflow-y-auto max-h-[30vh]">
              <div className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C] mb-2 border-b border-[#E2E0D8] pb-1">Update History Logs</div>
              <div className="space-y-2">
                {(trackerModal.updates || []).slice().reverse().map((up) => (
                  <div key={up.update_id} className="border border-[#E2E0D8] p-3 text-sm bg-white">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5" style={{ backgroundColor: trackerModal.status_colors?.[up.status] || '#0F0F0F' }}>{up.status}</span>
                      <span className="text-[11px] text-[#5C5C5C]">{new Date(up.at).toLocaleString()} by {up.by_email}</span>
                    </div>
                    <p className="mt-2 text-[#3a3a3a] leading-snug">{up.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company edit modal */}
      {editingCompany && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setEditingCompany(null)} data-testid="company-edit-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Edit company</div>
            <h3 className="font-display font-black text-2xl mt-2">{editingCompany.name}</h3>
            <div className="text-xs text-[#5C5C5C] font-mono mt-1">{editingCompany.company_id}</div>
            <div className="mt-5 space-y-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Name</label>
                <input data-testid="company-name" value={editingCompany.name || ""} onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Website</label>
                <input data-testid="company-website" value={editingCompany.website || ""} onChange={(e) => setEditingCompany({ ...editingCompany, website: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Location</label>
                <input data-testid="company-location" value={editingCompany.location || ""} onChange={(e) => setEditingCompany({ ...editingCompany, location: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Description</label>
                <textarea data-testid="company-description" value={editingCompany.description || ""} onChange={(e) => setEditingCompany({ ...editingCompany, description: e.target.value })} rows={3} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-[#0F0F0F]" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Company Founder / Owner User ID</label>
                <input data-testid="company-founder-id" value={editingCompany.owner_user_id || ""} onChange={(e) => setEditingCompany({ ...editingCompany, owner_user_id: e.target.value })} placeholder="e.g. user_abc123" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-[#0F0F0F]" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Company Team Members (comma separated User IDs)</label>
                <input
                  data-testid="company-members-list"
                  value={(editingCompany.members || []).join(", ")}
                  onChange={(e) => {
                    const nextMemb = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                    setEditingCompany({ ...editingCompany, members: nextMemb });
                  }}
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-[#0F0F0F]"
                  placeholder="e.g. user_1, user_2"
                />
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={saveCompany} data-testid="save-company" disabled={!editingCompany.name?.trim()} className="btn-terra flex-1"><Pencil className="w-4 h-4" /> Save</button>
                <button onClick={() => setEditingCompany(null)} className="btn-ghost">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send invite modal */}
      {sendingInvite && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setSendingInvite(null)} data-testid="invite-send-modal">
          <form onSubmit={doSendInvite} className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Mail className="w-3 h-3 text-[var(--brand-primary)]" /> Email invite</div>
            <h3 className="font-display font-black text-2xl mt-2">Send <code className="font-mono tracking-widest">{sendingInvite.code}</code></h3>
            <p className="text-sm text-[#5C5C5C] mt-1">Delivers the branded /invite/{sendingInvite.code} share-page link directly to their inbox.</p>
            <label className="label-eyebrow block mt-5 mb-1.5">Recipient email</label>
            <input data-testid="send-invite-email" type="email" required value={sendingInvite.to_email} onChange={(e) => setSendingInvite({ ...sendingInvite, to_email: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" placeholder="newhire@company.com" />
            <label className="label-eyebrow block mt-3 mb-1.5">Recipient name (optional)</label>
            <input data-testid="send-invite-name" value={sendingInvite.to_name} onChange={(e) => setSendingInvite({ ...sendingInvite, to_name: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
            {sendInviteError && <div data-testid="send-invite-error" className="text-sm text-[var(--brand-primary)] mt-2">{sendInviteError}</div>}
            <div className="flex gap-2 mt-5">
              <button disabled={sendInviteSubmitting || !sendingInvite.to_email} data-testid="send-invite-confirm" type="submit" className="btn-terra flex-1 disabled:opacity-50">
                <Mail className="w-4 h-4" /> {sendInviteSubmitting ? "Sending…" : "Send invite email"}
              </button>
              <button type="button" onClick={() => setSendingInvite(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* User edit modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setEditingUser(null)} data-testid="user-edit-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Pencil className="w-3 h-3" /> Edit user</div>
            <h3 className="font-display font-black text-2xl mt-2">{editingUser.name || editingUser.email}</h3>
            <div className="text-xs text-[#5C5C5C] font-mono mt-1">{editingUser.user_id}</div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="label-eyebrow block mb-1.5">Name</label>
                <input data-testid="edit-user-name" value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Email</label>
                <input data-testid="edit-user-email" type="email" value={editingUser.email} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Role</label>
                <select data-testid="edit-user-role" value={editingUser.role} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2">
                  <option value="candidate">candidate</option>
                  <option value="recruiter">recruiter</option>
                  <option value="moderator">moderator</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Approved</label>
                <select data-testid="edit-user-approved" value={editingUser.is_approved ? "1" : "0"} onChange={(e) => setEditingUser({ ...editingUser, is_approved: e.target.value === "1" })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2">
                  <option value="1">approved</option>
                  <option value="0">pending</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-1.5">Company name</label>
                <input data-testid="edit-user-company" value={editingUser.company_name} onChange={(e) => setEditingUser({ ...editingUser, company_name: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Job title</label>
                <input data-testid="edit-user-jobtitle" value={editingUser.job_title} onChange={(e) => setEditingUser({ ...editingUser, job_title: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Phone</label>
                <input data-testid="edit-user-phone" value={editingUser.phone} onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-1.5">LinkedIn URL</label>
                <input data-testid="edit-user-linkedin" value={editingUser.linkedin_url} onChange={(e) => setEditingUser({ ...editingUser, linkedin_url: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-1.5">Company website</label>
                <input data-testid="edit-user-website" value={editingUser.company_website} onChange={(e) => setEditingUser({ ...editingUser, company_website: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-1.5">Attach to existing company</label>
                <select data-testid="edit-user-attach-company" value={editingUser._attach_company_id || ""} onChange={(e) => setEditingUser({ ...editingUser, _attach_company_id: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2">
                  <option value="">— No company —</option>
                  {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name} ({c.company_id})</option>)}
                </select>
                <p className="text-[11px] text-[#5C5C5C] mt-1">Attaches the user as a recruiter on save. Their plan will auto-inherit from the company owner.</p>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Subscription tier</label>
                <select data-testid="edit-user-tier" value={editingUser.subscription_tier} onChange={(e) => setEditingUser({ ...editingUser, subscription_tier: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2">
                  <option value="free">free</option>
                  <option value="starter">starter</option>
                  <option value="growth">growth</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Tier expires on (optional)</label>
                <div className="flex gap-2">
                  <input
                    data-testid="edit-user-tier-ends-at"
                    type="date"
                    value={editingUser.subscription_ends_at || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, subscription_ends_at: e.target.value })}
                    className="flex-1 border border-[#0F0F0F] bg-transparent px-3 py-2"
                  />
                  {editingUser.subscription_ends_at && (
                    <button
                      type="button"
                      data-testid="edit-user-tier-ends-at-clear"
                      onClick={() => setEditingUser({ ...editingUser, subscription_ends_at: "" })}
                      className="btn-ghost"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-[#5C5C5C] mt-1">Leave blank for no expiry. On this date the user is auto-swept back to <strong>free</strong> and an audit entry is recorded. Reminder emails are sent to the user and you at 7 days and 1 day before expiry.</p>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Organisation type</label>
                <select data-testid="edit-user-orgtype" value={editingUser.org_type || "business"} onChange={(e) => setEditingUser({ ...editingUser, org_type: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2">
                  <option value="business">Real business</option>
                  <option value="game">Game group (disclaimer on listings)</option>
                </select>
                <p className="text-[11px] text-[#5C5C5C] mt-1">Toggling this auto-propagates to the user&apos;s company so every job from that recruiter inherits the &ldquo;Not affiliated with any official organisation&rdquo; notice.</p>
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-1.5">Custom Tags (comma separated)</label>
                <input
                  data-testid="edit-user-custom-tags"
                  type="text"
                  value={editingUser.custom_tags || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, custom_tags: e.target.value })}
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-[#0F0F0F]"
                  placeholder="e.g. Beta Tester, VIP Recruiter, Core Member"
                />
                <p className="text-[11px] text-[#5C5C5C] mt-1">Comma-separated badges displayed on user Settings, Billing limits, and Admin lists.</p>
              </div>
              <div className="col-span-2 bg-amber-50/20 border border-amber-600/10 p-3">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-semibold text-amber-900">
                  <input
                    data-testid="edit-user-send-email"
                    type="checkbox"
                    checked={!!editingUser.send_edit_notification_email}
                    onChange={(e) => setEditingUser({ ...editingUser, send_edit_notification_email: e.target.checked })}
                    className="accent-amber-600"
                  />
                  <span>Send detailed edit notification email to user account?</span>
                </label>
                <p className="text-[11px] text-[#5C5C5C] mt-1">Check this to dispatch an account update summary email. Leave unchecked to silently save to reduce email spam.</p>
              </div>
            </div>
            {editingUserError && <div data-testid="edit-user-error" className="text-sm text-[var(--brand-primary)] mt-3">{editingUserError}</div>}
            <div className="flex gap-2 mt-5">
              <button onClick={saveUserEdit} disabled={editingUserSaving} data-testid="save-user-edit" className="btn-terra flex-1 disabled:opacity-50">
                <Pencil className="w-4 h-4" /> {editingUserSaving ? "Saving…" : "Save changes"}
              </button>
              <button onClick={() => setEditingUser(null)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Per-user quota override modal */}
      {overrideTarget && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setOverrideTarget(null)} data-testid="override-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Sliders className="w-3 h-3 text-[var(--brand-primary)]" /> Custom plan quotas</div>
            <h3 className="font-display font-black text-2xl mt-2">{overrideTarget.name || overrideTarget.email}</h3>
            <p className="text-sm text-[#5C5C5C] mt-1">Plan: <strong className="capitalize">{overrideTarget.subscription_tier || "free"}</strong>. Overrides below take precedence over the plan&apos;s defaults. Leave blank to inherit. Use <code className="bg-[#E2E0D8] px-1">-1</code> for unlimited.</p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="label-eyebrow block mb-1.5">Max active jobs</label>
                <input data-testid="override-max-jobs" type="number" value={overrideForm.max_jobs} onChange={(e) => setOverrideForm({ ...overrideForm, max_jobs: e.target.value })} placeholder="inherit" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Max team seats</label>
                <input data-testid="override-max-seats" type="number" value={overrideForm.max_team_seats} onChange={(e) => setOverrideForm({ ...overrideForm, max_team_seats: e.target.value })} placeholder="inherit" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-1.5">Max applications / month</label>
                <input data-testid="override-max-apps" type="number" value={overrideForm.max_applications_per_month} onChange={(e) => setOverrideForm({ ...overrideForm, max_applications_per_month: e.target.value })} placeholder="inherit" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Featured boosts / mo</label>
                <input data-testid="override-featured" type="number" value={overrideForm.max_featured_boosts_per_month} onChange={(e) => setOverrideForm({ ...overrideForm, max_featured_boosts_per_month: e.target.value })} placeholder="inherit" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Spotlight boosts / mo</label>
                <input data-testid="override-spotlight" type="number" value={overrideForm.max_spotlight_boosts_per_month} onChange={(e) => setOverrideForm({ ...overrideForm, max_spotlight_boosts_per_month: e.target.value })} placeholder="inherit" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-1.5">Note (private — visible to other owners only)</label>
                <textarea data-testid="override-note" rows={2} value={overrideForm.note} onChange={(e) => setOverrideForm({ ...overrideForm, note: e.target.value })} placeholder="e.g. Paid £200 for extra 10 seats — invoice INV-2026-014" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
            </div>
            {/* Per-feature override toggles — grant or revoke paid-tier perks individually */}
            <div className="mt-5 border-t border-[#E2E0D8] pt-4">
              <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Sparkles className="w-3 h-3 text-[var(--brand-primary)]" /> Paid-tier features</div>
              <p className="text-xs text-[#5C5C5C] mb-3">Grant or revoke individual perks. <strong>Inherit</strong> uses the user&apos;s actual plan; <strong>On</strong> forces the feature on; <strong>Off</strong> revokes it even if their plan would normally grant it.</p>
              <div className="space-y-2">
                {[
                  { key: "ff_custom_email_templates", label: "Custom email templates", min: "Growth" },
                  { key: "ff_custom_careers_branding", label: "Custom careers page branding", min: "Growth" },
                  { key: "ff_bulk_actions", label: "Bulk applicant actions", min: "Enterprise" },
                  { key: "ff_whitelabel", label: "White-label (hide platform branding)", min: "Enterprise" },
                  { key: "ff_custom_pipeline", label: "Custom pipeline stages (Growth+)", min: "Growth" },
                  { key: "ff_company_tracking", label: "Company progress tracking", min: "Enterprise" },
                ].map((f) => (
                  <div key={f.key} className="grid grid-cols-12 items-center gap-2 text-sm">
                    <div className="col-span-7">
                      <div className="font-semibold">{f.label}</div>
                      <div className="text-[10px] text-[#5C5C5C] uppercase tracking-widest">Default tier: {f.min}+</div>
                    </div>
                    <div className="col-span-5 grid grid-cols-3 border border-[#0F0F0F]">
                      {[
                        { v: "inherit", t: "Inherit" },
                        { v: "on", t: "On" },
                        { v: "off", t: "Off" },
                      ].map(({ v, t }) => (
                        <button
                          key={v}
                          type="button"
                          data-testid={`${f.key}-${v}`}
                          onClick={() => setOverrideForm({ ...overrideForm, [f.key]: v })}
                          className={`text-[11px] py-1.5 ${overrideForm[f.key] === v ? "bg-[#0F0F0F] text-white" : "bg-transparent hover:bg-[#F3F2EB]"}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Expiry dates — separate for Quotas vs Features as requested. Leave blank for permanent. */}
            <div className="mt-5 border-t border-[#E2E0D8] pt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="label-eyebrow block mb-1.5">Quotas expire on <span className="opacity-50 normal-case tracking-normal">(optional)</span></label>
                <input data-testid="override-quotas-expiry" type="date" min={new Date().toISOString().slice(0, 10)} value={overrideForm.quotas_expires_at} onChange={(e) => setOverrideForm({ ...overrideForm, quotas_expires_at: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Features expire on <span className="opacity-50 normal-case tracking-normal">(optional)</span></label>
                <input data-testid="override-features-expiry" type="date" min={new Date().toISOString().slice(0, 10)} value={overrideForm.features_expires_at} onChange={(e) => setOverrideForm({ ...overrideForm, features_expires_at: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <p className="col-span-2 text-[11px] text-[#5C5C5C]">On the expiry date the override is HARD-cleared — the user falls straight back to their plan defaults. Leave blank for a permanent grant.</p>
            </div>
            <div className="mt-3 border border-[var(--brand-primary)]/30 bg-[var(--brand-primary)]/5 p-3 flex items-start gap-2 text-xs">
              <Sparkles className="w-3.5 h-3.5 text-[var(--brand-primary)] mt-0.5 shrink-0" />
              <div>These limits are emphasised on the user&apos;s quota page (<code>/billing</code>) with a &ldquo;Custom&rdquo; badge so they always see what they&apos;re entitled to.</div>
            </div>
            {overrideError && <div data-testid="override-error" className="text-sm text-[var(--brand-primary)] mt-3">{overrideError}</div>}
            <div className="flex gap-2 mt-5">
              <button onClick={saveOverride} disabled={overrideSaving} data-testid="save-override" className="btn-terra flex-1 disabled:opacity-50">
                <ShieldCheck className="w-4 h-4" /> {overrideSaving ? "Saving…" : "Save overrides"}
              </button>
              <button onClick={notifyAward} disabled={overrideSaving} type="button" data-testid="notify-award" title="Open the email composer pre-filled with an 'award' template — Owner can edit before sending" className="btn-ghost text-sm">
                <Mail className="w-4 h-4" /> Notify by email
              </button>
              <button onClick={clearOverrides} disabled={overrideSaving} data-testid="clear-override" className="btn-ghost text-sm">
                <RefreshCcw className="w-4 h-4" /> Clear all
              </button>
              <button onClick={() => setOverrideTarget(null)} className="btn-ghost text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Report detail modal */}
      {editingReport && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setEditingReport(null)} data-testid="report-detail-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Flag className="w-3 h-3" /> Report detail</div>
            <h3 className="font-display font-black text-2xl mt-2">{editingReport.reason}</h3>
            <div className="text-sm text-[#5C5C5C]">{editingReport.target_type} · <code>{editingReport.target_id}</code> · {editingReport.status}</div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Reason</label>
                <input data-testid="edit-report-reason" value={editingReport.reason} onChange={(e) => setEditingReport({ ...editingReport, reason: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Detail</label>
                <textarea data-testid="edit-report-detail" rows={3} value={editingReport.detail || ""} onChange={(e) => setEditingReport({ ...editingReport, detail: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => editReport({ reason: editingReport.reason, detail: editingReport.detail })} data-testid="save-report-edit" className="btn-ink text-sm">Save changes</button>
                {editingReport.status === "resolved" && (
                  <button onClick={async () => { await reopenReport(editingReport.report_id); const r = await api.get("/admin/reports"); setReports(r.data); setEditingReport(r.data.find((x) => x.report_id === editingReport.report_id)); }} data-testid="reopen-in-modal" className="text-sm border border-[var(--brand-primary)] text-[var(--brand-primary)] px-3 py-1.5"><RefreshCcw className="w-3.5 h-3.5 inline mr-1" /> Reopen</button>
                )}
              </div>
            </div>

            <div className="mt-6">
              <div className="label-eyebrow">Notes timeline ({editingReport.notes?.length || 0})</div>
              <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                {(editingReport.notes || []).slice().reverse().map((n) => (
                  <div key={n.note_id} className="border border-[#E2E0D8] p-3">
                    <div className="text-xs text-[#5C5C5C]">{n.by_email} · {new Date(n.at).toLocaleString()}</div>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{n.text}</p>
                  </div>
                ))}
                {(editingReport.notes?.length || 0) === 0 && <div className="text-sm text-[#5C5C5C]">No notes yet.</div>}
              </div>
              <div className="mt-3 flex gap-2">
                <input data-testid="report-note-input" value={reportNote} onChange={(e) => setReportNote(e.target.value)} placeholder="Add a note…" className="flex-1 border border-[#0F0F0F] bg-transparent px-3 py-2" />
                <button onClick={addReportNote} disabled={!reportNote.trim()} data-testid="add-report-note" className="btn-terra text-sm py-2"><Plus className="w-4 h-4" /> Add</button>
              </div>
            </div>
            <button onClick={() => setEditingReport(null)} className="mt-6 btn-ghost text-sm">Close</button>
          </div>
        </div>
      )}

      {/* Admin Contract Viewer Modal */}
      {adminSelectedContract && (
        <div className="fixed inset-0 bg-[#0F0F0F]/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#FDFCF8] border-2 border-[#0F0F0F] w-full max-w-3xl p-6 sm:p-8 max-h-[90vh] flex flex-col shadow-2xl relative rounded-none text-left">
            <button onClick={() => setAdminSelectedContract(null)} className="absolute top-4 right-4 text-[#5C5C5C] hover:text-[#0F0F0F]">
              <X className="w-6 h-6" />
            </button>
            <div className="label-eyebrow flex items-center gap-1.5 text-green-700">
              <ShieldCheck className="w-4 h-4" /> Executed 12-Month Contract
            </div>
            <h2 className="font-display font-black text-2xl mt-1 tracking-tight">
              Subscriber Agreement
            </h2>
            <div className="mt-2 text-xs text-[#5C5C5C] flex flex-wrap gap-x-4 gap-y-1">
              <span>Contract ID: <strong>{adminSelectedContract.contract_id}</strong></span>
              <span>Signed At: <strong>{new Date(adminSelectedContract.signed_at).toLocaleString()}</strong></span>
            </div>

            <div className="mt-4 flex-1 overflow-y-auto border border-[#E2E0D8] p-4 bg-[#F3F2EB] font-mono text-xs whitespace-pre-wrap leading-relaxed max-h-[50vh] text-[#0F0F0F]">
              {adminSelectedContract.contract_text}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setAdminSelectedContract(null)}
                className="btn-ink px-6 py-2 text-sm font-semibold"
              >
                Close Agreement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contract Term Edit Modal */}
      {editingContractTerm && (
        <div className="fixed inset-0 bg-[#0F0F0F]/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#FDFCF8] border-2 border-[#0F0F0F] w-full max-w-md p-6 shadow-2xl relative rounded-none text-left">
            <button onClick={() => setEditingContractTerm(null)} className="absolute top-4 right-4 text-[#5C5C5C] hover:text-[#0F0F0F]">
              <X className="w-6 h-6" />
            </button>
            <div className="label-eyebrow flex items-center gap-1.5 text-amber-700">
              <Pencil className="w-3.5 h-3.5" /> Edit Contract Term
            </div>
            <h2 className="font-display font-black text-xl mt-1 tracking-tight">
              Edit Term Dates
            </h2>
            <p className="text-xs text-[#5C5C5C] mt-2">
              Modify the start and end dates of this 12-Month contract. This will also update the user&apos;s subscription end date on the platform.
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={editContractStartDate}
                  onChange={(e) => setEditContractStartDate(e.target.value)}
                  data-testid="contract-term-start-date"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F]"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={editContractEndDate}
                  onChange={(e) => setEditContractEndDate(e.target.value)}
                  data-testid="contract-term-end-date"
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F]"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setEditingContractTerm(null)}
                className="px-4 py-1.5 text-sm font-semibold border border-[#0F0F0F] hover:bg-[#0F0F0F]/5 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveContractTerm}
                disabled={updatingTerm}
                data-testid="save-contract-term-btn"
                className="bg-amber-600 text-white px-5 py-1.5 text-sm font-semibold hover:bg-amber-700 disabled:opacity-40 transition flex items-center gap-2"
              >
                {updatingTerm ? "Updating..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
